import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useOverlayDismiss } from '../lib/overlayDismiss';
import { useTableSort, sortIndicator } from '../lib/tableSort';
import { usersService, User, UserPermission, PermissionPageType } from '../services/users.service';
import { dailyReportService, DailyReportCollectionLog, CollectDailyReportResult } from '../services/daily-report.service';
import { dlpService, DlpBuyLicense, DlpExceptionRequest, AdminIssueSite, AdminCommonBlockedSite, AdminLicenseStat, AdminExEmployee } from '../services/dlp.service';
import { nacService } from '../services/nac.service';
import { companyService, CompanyInfo } from '../services/company.service';
import WriteCompleteBar from '../components/report/WriteCompleteBar';
import { resultsService } from '../services/results.service';
import { logsService, Log, LogAction, LOG_ACTION_LABELS } from '../services/logs.service';
import { settingsService, PortalSetting } from '../services/settings.service';
import { useSettings, ThemeType, DesignVersion } from '../contexts/SettingsContext';
import { useAuth } from '../contexts/AuthContext';
import {
  securityExceptionService,
  ExceptionItem,
  NacExceptionRequest,
  SiteExceptionRequest,
  DlpExceptionRequest as SecurityDlpExceptionRequest,
  SolutionType,
  NAC_EXCEPTION_ITEMS,
  SITE_EXCEPTION_ITEMS,
  SITE_EXCEPTION_DETAILS,
  DLP_EXCEPTION_MAP,
  DLP_EXCEPTION_ITEMS,
  SOLUTION_USEYN_KEY,
  ExceptionCompany,
} from '../services/security-exception.service';
import './AdminPage.css';
import { ADMIN_TABS, hasTabs, resolveActiveTab } from './adminTabs';
import WebFilterManagementSection from '../components/admin/web-filter/WebFilterManagementSection';
import EdrManagementSection from '../components/admin/edr/EdrManagementSection';
import ReportReviewSection from '../components/admin/report-review/ReportReviewSection';
import { isSteelCompany } from '../lib/dlpSteelTable';
import NacExceptionSection from '../components/nac/NacExceptionSection';
import WfExceptionSection from '../components/admin/web-filter/WfExceptionSection';
import ExceptionActorSection from '../components/admin/exception/ExceptionActorSection';
import MetaMappingSection from '../components/admin/meta/MetaMappingSection';
import BulkUploadBar from '../components/admin/common/BulkUploadBar';
import { EXCEPTION_SAMPLE_CSV } from '../lib/exceptionSampleCsv';
import { isAllowedActor } from '../lib/exceptionOptions';
import { toActorRejection } from '../lib/actorRejection';
import { exceptionActorService } from '../services/exception-actor.service';
import {
  SectionHeader as ExHeader, SearchBar as ExSearchBar, FormCard as ExFormCard,
  Fieldset as ExFieldset, Field as ExField, TableShell as ExTableShell,
  SortableTh as ExSortableTh, RowActions as ExRowActions,
  LoadingState as ExLoading, EmptyState as ExEmpty,
  Input as UiInput, Select as UiSelect, Button as UiButton,
} from '../components/ui';
// 남은 인라인 스타일 상수(폼/셀 세부) — 후속 증분에서 완전 토큰화 예정
import {
  fldStyle as exFld, exThStyle as exTh, exTdStyle as exTd, exRowBg as exBg,
} from '../components/admin/exception/exceptionUi';

type AdminCategory = 'USER' | 'APT' | 'DLP' | 'NAC' | 'LOG' | 'SETTINGS' | 'DAILY_REPORT' | 'EXCEPTION' | 'WEB_FILTER' | 'META' | 'EDR' | 'REPORT_REVIEW';

const AdminPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = (searchParams.get('category') as AdminCategory) || 'USER';
  const { user: currentUser, impersonate } = useAuth();
  const tab = searchParams.get('tab') || '';
  
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(
    null
  );

  useEffect(() => {
    if (category === 'USER') {
      loadUsers();
    }
    // URL 파라미터가 없으면 기본값 설정
    if (!searchParams.get('category')) {
      setSearchParams({ category: 'USER' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  useEffect(() => {
    if (category === 'SETTINGS') {
      return; // SettingsContext에서 자동 로드
    }
  }, [category]);

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const data = await usersService.getAll();
      setUsers(data);
    } catch (error) {
      console.error('Failed to load users:', error);
      setMessage({ type: 'error', text: '사용자 목록을 불러오는데 실패했습니다.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRoleChange = async (userId: number, newRole: 'admin' | 'editor' | 'reviewer' | 'viewer') => {
    try {
      await usersService.updateRole(userId, { role: newRole });
      setMessage({ type: 'success', text: '권한이 변경되었습니다.' });
      loadUsers();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || '권한 변경에 실패했습니다.',
      });
    }
  };

  const handleDelete = async (userId: number, userEmail: string) => {
    if (!window.confirm(`정말로 ${userEmail} 사용자를 삭제하시겠습니까?`)) {
      return;
    }

    try {
      await usersService.delete(userId);
      setMessage({ type: 'success', text: '사용자가 삭제되었습니다.' });
      loadUsers();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || '사용자 삭제에 실패했습니다.',
      });
    }
  };

  const handleTabChange = (newTab: string) => {
    setSearchParams({ category, tab: newTab });
  };

  // 카테고리별 기본 탭 결정 — 정의는 adminTabs.ts 한곳에 모아둔다
  const activeTab = resolveActiveTab(category, tab);

  return (
    <div className="admin-page">
      <h1>관리자 페이지</h1>

      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
          <button onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      <div className="admin-content-area">
          {/* 탭 메뉴 — 탭이 정의된 카테고리에만 그린다 (adminTabs.ts) */}
          {hasTabs(category) && (
            <div className="admin-tabs">
              {ADMIN_TABS[category].map((t) => (
                <button
                  key={t.id}
                  className={`admin-tab ${activeTab === t.id ? 'active' : ''}`}
                  onClick={() => handleTabChange(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {/* 컨텐츠 */}
          <div className="admin-tab-content">
            {category === 'USER' && (
              <UserManagementSection
                users={users}
                isLoading={isLoading}
                onRoleChange={handleRoleChange}
                onDelete={handleDelete}
                onReload={loadUsers}
                onImpersonate={impersonate}
                currentUserId={currentUser?.id}
              />
            )}
            {category === 'APT' && (
              <>
                <AptDataUploadSection />
                <div style={{ marginTop: '3rem' }} />
                <AptSpamReportSection />
              </>
            )}
            {category === 'DLP' && activeTab === 'dlp-report' && <DlpReportManagementSection />}
            {category === 'DLP' && activeTab === 'dlp-license' && <DlpLicenseManagementSection />}
            {category === 'NAC' && activeTab === 'nac-report' && <NacReportManagementSection />}
            {category === 'WEB_FILTER' && <WebFilterManagementSection activeTab={activeTab} />}
            {category === 'LOG' && <LogManagementSection />}
            {category === 'SETTINGS' && <PortalSettingsSection />}
            {category === 'DAILY_REPORT' && <DailyReportManagementSection />}
            {category === 'META' && <MetaMappingSection />}
            {category === 'EXCEPTION' && activeTab === 'exception-security' && <SecurityExceptionManagementSection />}
            {category === 'EXCEPTION' && activeTab === 'exception-dlp' && <DlpExceptionManagementSection />}
            {category === 'EXCEPTION' && activeTab === 'exception-nac' && <NacExceptionSection />}
            {category === 'EXCEPTION' && activeTab === 'exception-wf' && <WfExceptionSection />}
            {category === 'EXCEPTION' && activeTab === 'exception-actor' && <ExceptionActorSection />}
            {category === 'EDR' && <EdrManagementSection activeTab={activeTab} />}
            {category === 'REPORT_REVIEW' && <ReportReviewSection />}
          </div>
      </div>
    </div>
  );
};

// 사전 등록 모달
const PreRegisterModal: React.FC<{
  onClose: () => void;
  onSuccess: () => void;
}> = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    department: '',
    role: 'viewer' as 'admin' | 'editor' | 'reviewer' | 'viewer',
  });
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim() || !formData.email.trim() || !formData.company.trim()) {
      setError('모든 필드를 입력해주세요.');
      return;
    }

    const emailDomain = formData.email.split('@')[1]?.toLowerCase();
    const allowedDomains = ['vntgcorp.com', 'seah.co.kr', 'ctckorea.com', 'irongrey.co', 'dongasteel.co.kr'];
    if (!allowedDomains.includes(emailDomain)) {
      setError(`${allowedDomains.join(', ')} 도메인만 등록 가능합니다.`);
      return;
    }

    setIsSaving(true);
    try {
      await usersService.preRegister(formData);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || '사전 등록에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>사용자 사전 등록</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <p style={{ color: 'var(--muted-foreground, #666)', marginBottom: '1rem', fontSize: '0.9rem' }}>
              Google OAuth 로그인 시 이메일로 자동 매칭됩니다.
            </p>
            {error && <div className="error-message">{error}</div>}
            <div className="form-group">
              <label htmlFor="pre-name">이름</label>
              <input
                id="pre-name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="예: 홍길동"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="pre-email">이메일</label>
              <input
                id="pre-email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="예: user@vntgcorp.com / user@ctckorea.com"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="pre-company">회사명</label>
              <input
                id="pre-company"
                name="company"
                type="text"
                value={formData.company}
                onChange={handleChange}
                placeholder="예: 세아그룹, VNTG"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="pre-department">부서명</label>
              <input
                id="pre-department"
                name="department"
                type="text"
                value={formData.department}
                onChange={handleChange}
                placeholder="예: 정보보호팀"
              />
            </div>
            <div className="form-group">
              <label htmlFor="pre-role">권한</label>
              <div className="role-selector">
                {(['viewer', 'reviewer', 'editor', 'admin'] as const).map((role) => (
                  <button
                    key={role}
                    type="button"
                    className={`role-option ${formData.role === role ? 'selected' : ''} role-${role}`}
                    onClick={() => setFormData({ ...formData, role })}
                  >
                    <span className="role-option-label">{role}</span>
                    <span className="role-option-desc">
                      {role === 'viewer' && '읽기 전용'}
                      {role === 'reviewer' && '검토자 (데모)'}
                      {role === 'editor' && '편집자'}
                      {role === 'admin' && '관리자'}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>취소</button>
            <button type="submit" className="btn-primary" disabled={isSaving}>
              {isSaving ? '등록 중...' : '등록'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const USERS_PER_PAGE = 20;

type RoleFilter = 'all' | 'admin' | 'editor' | 'reviewer' | 'viewer';

// 유저관리 섹션
const UserManagementSection: React.FC<{
  users: User[];
  isLoading: boolean;
  onRoleChange: (userId: number, role: 'admin' | 'editor' | 'reviewer' | 'viewer') => void;
  onDelete: (userId: number, userEmail: string) => void;
  onReload: () => void;
  onImpersonate: (userId: number) => void;
  currentUserId: number | undefined;
}> = ({ users, isLoading, onRoleChange, onDelete, onReload, onImpersonate, currentUserId }) => {
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [showPreRegisterModal, setShowPreRegisterModal] = useState(false);
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const filteredUsers = users
    .filter((u) => roleFilter === 'all' || u.role === roleFilter)
    .filter((u) => {
      const q = searchQuery.trim().toLowerCase();
      if (!q) return true;
      return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
    });
  // IPT-556 이슈4 — 컬럼 정렬(전체 필터 결과 기준 정렬 후 페이지 분할)
  const { sorted: sortedUsers, sortKey, sortDir, onSort } = useTableSort<User>(filteredUsers);
  const totalPages = Math.max(1, Math.ceil(sortedUsers.length / USERS_PER_PAGE));
  const pagedUsers = sortedUsers.slice((currentPage - 1) * USERS_PER_PAGE, currentPage * USERS_PER_PAGE);
  const userTh = (key: keyof User, label: string) => (
    <th onClick={() => onSort(key)} style={{ cursor: 'pointer', userSelect: 'none' }}>
      {label} {sortIndicator(sortKey, key, sortDir)}
    </th>
  );

  const handleRoleFilterChange = (filter: RoleFilter) => {
    setRoleFilter(filter);
    setCurrentPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 style={{ margin: 0 }}>유저관리</h2>
        <button
          className="btn-primary"
          onClick={() => setShowPreRegisterModal(true)}
        >
          + 사용자 사전 등록
        </button>
      </div>

      {/* 이름/이메일 검색 */}
      <div style={{ marginBottom: '0.75rem' }}>
        <input
          type="text"
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder="이름 또는 이메일로 검색..."
          style={{
            width: '300px',
            padding: '0.5rem 0.75rem',
            border: '1px solid var(--card-border)',
            borderRadius: '6px',
            fontSize: '0.875rem',
            outline: 'none',
          }}
        />
        {searchQuery && (
          <button
            onClick={() => { setSearchQuery(''); setCurrentPage(1); }}
            style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: 'var(--muted-foreground)', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            ✕ 초기화
          </button>
        )}
      </div>

      {/* Role 필터 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
        <span style={{ fontSize: '0.875rem', color: 'var(--muted-foreground, #555)', fontWeight: 500 }}>권한 필터:</span>
        {(['all', 'admin', 'editor', 'reviewer', 'viewer'] as RoleFilter[]).map((r) => (
          <button
            key={r}
            onClick={() => handleRoleFilterChange(r)}
            className={roleFilter === r ? `role-filter-btn active role-filter-${r}` : `role-filter-btn role-filter-${r}`}
          >
            {r === 'all' ? '전체' : r}
            {r !== 'all' && (
              <span style={{ marginLeft: '0.25rem', opacity: 0.75 }}>
                ({users.filter((u) => u.role === r).length})
              </span>
            )}
            {r === 'all' && (
              <span style={{ marginLeft: '0.25rem', opacity: 0.75 }}>({users.length})</span>
            )}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="loading">사용자 목록을 불러오는 중...</div>
      ) : (
        <>
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  {userTh('id', 'ID')}
                  {userTh('name', '이름')}
                  {userTh('company', '회사')}
                  {userTh('department', '부서')}
                  {userTh('email', '이메일')}
                  {userTh('role', '권한')}
                  {userTh('createdAt', '가입일')}
                  {userTh('lastLoginAt', '최종 로그인')}
                  <th>액션</th>
                </tr>
              </thead>
              <tbody>
                {pagedUsers.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="no-data">
                      {searchQuery ? `'${searchQuery}' 검색 결과가 없습니다.` : roleFilter === 'all' ? '등록된 사용자가 없습니다.' : `${roleFilter} 권한의 사용자가 없습니다.`}
                    </td>
                  </tr>
                ) : (
                  pagedUsers.map((user) => (
                    <tr key={user.id}>
                      <td>{user.id}</td>
                      <td>
                        {user.name}
                        {user.authProvider === 'google' && !user.googleId && (
                          <span className="status-badge status-pending" title="Google 로그인 대기 중"> (대기 중)</span>
                        )}
                      </td>
                      <td>{user.company}</td>
                      <td>{user.department}</td>
                      <td>{user.email}</td>
                      <td>
                        <span className={`role-badge role-${user.role}`}>{user.role}</span>
                      </td>
                      <td>{new Date(user.createdAt).toLocaleDateString('ko-KR')}</td>
                      <td>{user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString('ko-KR') : '-'}</td>
                      <td>
                        <div className="action-buttons">
                          <select
                            value={user.role}
                            onChange={(e) =>
                              onRoleChange(
                                user.id,
                                e.target.value as 'admin' | 'editor' | 'reviewer' | 'viewer'
                              )
                            }
                            className="role-select"
                          >
                            <option value="viewer">viewer</option>
                            <option value="reviewer">reviewer</option>
                            <option value="editor">editor</option>
                            <option value="admin">admin</option>
                          </select>
                          <button
                            className="btn-secondary"
                            onClick={() => {
                              setSelectedUser(user);
                              setShowPermissionModal(true);
                            }}
                          >
                            권한
                          </button>
                          <button
                            className="btn-danger"
                            onClick={() => onDelete(user.id, user.email)}
                          >
                            삭제
                          </button>
                          {user.role !== 'admin' && user.id !== currentUserId && (
                            <button
                              className="btn-impersonate"
                              onClick={() => onImpersonate(user.id)}
                              title={`${user.name} (${user.role}) 으로 보기`}
                            >
                              👁 보기
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                «
              </button>
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                ‹
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                  if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((item, idx) =>
                  item === '...' ? (
                    <span key={`ellipsis-${idx}`} className="pagination-ellipsis">…</span>
                  ) : (
                    <button
                      key={item}
                      className={`pagination-btn ${currentPage === item ? 'active' : ''}`}
                      onClick={() => setCurrentPage(item as number)}
                    >
                      {item}
                    </button>
                  )
                )}
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                ›
              </button>
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                »
              </button>
              <span className="pagination-info">
                {(currentPage - 1) * USERS_PER_PAGE + 1}–{Math.min(currentPage * USERS_PER_PAGE, filteredUsers.length)} / {filteredUsers.length}명
              </span>
            </div>
          )}
        </>
      )}

      {showPermissionModal && selectedUser && (
        <PermissionModal
          user={selectedUser}
          onClose={() => {
            setShowPermissionModal(false);
            setSelectedUser(null);
          }}
        />
      )}

      {showPreRegisterModal && (
        <PreRegisterModal
          onClose={() => setShowPreRegisterModal(false)}
          onSuccess={onReload}
        />
      )}
    </div>
  );
};

// 권한 설정 모달
type PageTypeKey = PermissionPageType;

/** 권한 팝업 드롭다운 항목 — 순서가 화면 순서다. EDR 은 화면별로 분리돼 있다 (IPT-628) */
const PAGE_TYPE_OPTIONS: ReadonlyArray<{ key: PageTypeKey; label: string }> = [
  { key: 'APT', label: 'APT' },
  { key: 'DLP', label: 'DLP' },
  { key: 'NAC', label: 'NAC' },
  { key: 'WEB_FILTER', label: '유해차단' },
  { key: 'DAILY_REPORT', label: '일일보고서' },
  { key: 'EDR_THREAT', label: 'EDR 위협탐지 안내' },
  { key: 'EDR_REPORT', label: 'EDR 운영보고서' },
];

const buildByPageType = <T,>(make: (key: PageTypeKey) => T): Record<PageTypeKey, T> =>
  Object.fromEntries(PAGE_TYPE_OPTIONS.map((o) => [o.key, make(o.key)])) as Record<PageTypeKey, T>;
const SEAHSTEEL_COMPANIES = ['세아제강지주', '세아제강', '동아스틸', '세아씨엠'];

export const PermissionModal: React.FC<{
  user: User;
  onClose: () => void;
}> = ({ user, onClose }) => {
  const [availableCompaniesMap, setAvailableCompaniesMap] = useState<Record<PageTypeKey, CompanyInfo[]>>(buildByPageType(() => []));
  const [selectedCompaniesMap, setSelectedCompaniesMap] = useState<Record<PageTypeKey, Set<string>>>(buildByPageType(() => new Set<string>()));
  const [selectedPageType, setSelectedPageType] = useState<PageTypeKey>('APT');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // 로드에 실패한 채 저장하면 빈 목록이 전송돼 사용자의 권한이 전부 삭제된다 — 저장을 막는다
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  const loadAllData = async () => {
    setIsLoading(true);
    try {
      const lists = await Promise.all(PAGE_TYPE_OPTIONS.map((o) => companyService.getByPageType(o.key)));
      const companiesByType = Object.fromEntries(
        PAGE_TYPE_OPTIONS.map((o, idx) => [o.key, lists[idx]]),
      ) as Record<PageTypeKey, CompanyInfo[]>;

      setAvailableCompaniesMap(companiesByType);

      if (user.role === 'admin' || user.role === 'editor' || user.role === 'reviewer') {
        setSelectedCompaniesMap(
          buildByPageType((key) => new Set(companiesByType[key].map((c) => c.companyName))),
        );
      } else {
        const userPerms = await usersService.getUserPermissions(user.id);
        setSelectedCompaniesMap(
          buildByPageType((key) => new Set(userPerms.filter((p) => p.pageType === key).map((p) => p.companyName))),
        );
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      setLoadFailed(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompanyToggle = (companyName: string) => {
    const newSet = new Set(selectedCompaniesMap[selectedPageType]);
    if (newSet.has(companyName)) newSet.delete(companyName);
    else newSet.add(companyName);
    setSelectedCompaniesMap({ ...selectedCompaniesMap, [selectedPageType]: newSet });
  };

  const handleSelectAll = () => {
    const current = selectedCompaniesMap[selectedPageType];
    const newSet = current.size === filteredCompanies.length
      ? new Set<string>()
      : new Set(filteredCompanies.map((c) => c.companyName));
    setSelectedCompaniesMap({ ...selectedCompaniesMap, [selectedPageType]: newSet });
  };

  const handleDailyReportGroupSelect = (groupCompanies: string[]) => {
    const newSet = new Set(selectedCompaniesMap.DAILY_REPORT);
    const allSelected = groupCompanies.every((c) => newSet.has(c));
    if (allSelected) {
      groupCompanies.forEach((c) => newSet.delete(c));
    } else {
      groupCompanies.forEach((c) => newSet.add(c));
    }
    setSelectedCompaniesMap({ ...selectedCompaniesMap, DAILY_REPORT: newSet });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const selectedPermissions = PAGE_TYPE_OPTIONS.flatMap(({ key }) =>
        Array.from(selectedCompaniesMap[key]).map((companyName) => ({ companyName, pageType: key })),
      );

      await usersService.setUserPermissions(user.id, selectedPermissions);
      alert('권한이 저장되었습니다.');
      onClose();
    } catch (error: any) {
      alert('권한 저장에 실패했습니다: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  const availableCompanies = availableCompaniesMap[selectedPageType];
  const selectedCompanies = selectedCompaniesMap[selectedPageType];

  const filteredCompanies = availableCompanies.filter((company) =>
    company.companyName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isAdminOrEditorOrReviewer = user.role === 'admin' || user.role === 'editor' || user.role === 'reviewer';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>권한 설정 - {user.email} ({user.role})</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {isAdminOrEditorOrReviewer && (
            <div className="permission-info-banner">
              ℹ️ {user.role === 'admin' ? 'Admin' : user.role === 'editor' ? 'Editor' : 'Reviewer'}은(는) 모든 회사의 모든 페이지에 대한 권한을 가집니다.
            </div>
          )}
          <div className="page-type-selector">
            <label>Page Type:</label>
            <select
              value={selectedPageType}
              onChange={(e) => {
                setSelectedPageType(e.target.value as PageTypeKey);
                setSearchTerm('');
              }}
            >
              {PAGE_TYPE_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          {isLoading ? (
            <div className="loading">로딩 중...</div>
          ) : (
            <>
              {selectedPageType === 'DAILY_REPORT' && (
                <div className="daily-report-group-shortcuts">
                  <span className="shortcut-label">계열 일괄선택:</span>
                  {[
                    { label: '세아홀딩스계열', companies: availableCompanies.map((c) => c.companyName).filter((n) => !SEAHSTEEL_COMPANIES.includes(n)) },
                    { label: '세아제강계열', companies: SEAHSTEEL_COMPANIES.filter((n) => availableCompanies.some((c) => c.companyName === n)) },
                  ].map(({ label, companies }) => (
                    <button
                      key={label}
                      className="btn-group-shortcut"
                      onClick={() => handleDailyReportGroupSelect(companies)}
                      disabled={isAdminOrEditorOrReviewer || companies.length === 0}
                    >
                      {label}
                    </button>
                  ))}
                  <button
                    className="btn-group-shortcut"
                    onClick={() => handleDailyReportGroupSelect(availableCompanies.map((c) => c.companyName))}
                    disabled={isAdminOrEditorOrReviewer}
                  >
                    모두
                  </button>
                </div>
              )}
              <div className="permission-search">
                <input
                  type="text"
                  placeholder="회사명 검색..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                  disabled={isAdminOrEditorOrReviewer}
                />
                <button
                  className="btn-select-all"
                  onClick={handleSelectAll}
                  disabled={isAdminOrEditorOrReviewer}
                >
                  {selectedCompanies.size === filteredCompanies.length ? '전체 해제' : '전체 선택'}
                </button>
              </div>
              <div className="permission-list">
                {filteredCompanies.length === 0 ? (
                  <p className="no-companies">해당 Page Type에 사용 가능한 회사가 없습니다.</p>
                ) : (
                  <div className="company-checkbox-list">
                    {filteredCompanies.map((company) => (
                      <label key={company.orgCode} className="company-checkbox-item">
                        <input
                          type="checkbox"
                          checked={selectedCompanies.has(company.companyName)}
                          onChange={() => handleCompanyToggle(company.companyName)}
                          disabled={isAdminOrEditorOrReviewer}
                        />
                        <span>{company.companyName}</span>
                      </label>
                    ))}
                  </div>
                )}
                <div className="selected-count">
                  선택된 회사: {selectedCompanies.size}개 / {filteredCompanies.length}개
                </div>
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            {isAdminOrEditorOrReviewer ? '닫기' : '취소'}
          </button>
          {!isAdminOrEditorOrReviewer && (
            <button className="btn-primary" onClick={handleSave} disabled={isSaving || isLoading || loadFailed}>
              {isSaving ? '저장 중...' : '저장'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// DLP 운영보고서 관리 섹션
export const DlpReportManagementSection: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [availablePeriods, setAvailablePeriods] = useState<string[]>([]);
  const [availableCompanies, setAvailableCompanies] = useState<CompanyInfo[]>([]);
  const [summaryContent, setSummaryContent] = useState<string>('');
  // 고정된 링크 목록 (base-rule.mdc에 정의된 link_id)
  // 세아제강 계열은 "사외메신저/생성형AI 업로드 현황" 표에 Raw Data 엑셀 링크가 별도로 필요하다
  // (IPT-441 TRD §3.2, Confluence 요구사항정의 첨부 스크린샷의 "(링크)" 표기 확인)
  const fixedLinks = isSteelCompany(selectedCompany)
    ? [
        { linkId: 'steel_messenger_upload_raw', linkName: '사외메신저 업로드 Raw Data' },
        { linkId: 'steel_genai_upload_raw', linkName: '생성형AI 업로드 Raw Data' },
      ]
    : [
        { linkId: 'dlp_ops_data', linkName: 'DLP 운영데이터' },
      ];

  const [attachments, setAttachments] = useState<Record<string, { linkId: string; linkName: string; linkUrl: string; id?: number; saved?: boolean }>>({});
  const [savingLinks, setSavingLinks] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  const { user } = useAuth();

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedPeriod && selectedCompany) {
      loadReportData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod, selectedCompany]);

  const loadInitialData = async () => {
    setIsLoading(true);
    try {
      const periods = await dlpService.getAvailablePeriods();
      setAvailablePeriods(periods);
      if (periods.length > 0) {
        setSelectedPeriod(periods[0]);
      }

      const companies = await companyService.getByPageType('DLP');
      setAvailableCompanies(companies);
      if (companies.length === 1) {
        setSelectedCompany(companies[0].companyName);
      }
    } catch (error) {
      console.error('Failed to load initial data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadReportData = async () => {
    if (!selectedPeriod || !selectedCompany) return;

    setIsLoading(true);
    try {
      // 요약 내용 로드
      const summary = await dlpService.getSummary(selectedPeriod, selectedCompany);
      setSummaryContent(summary?.summaryContent || '');

      // 링크 목록 로드
      const links = await dlpService.getAttachments(selectedPeriod, selectedCompany);
      const linksMap: Record<string, { linkId: string; linkName: string; linkUrl: string; id?: number; saved?: boolean }> = {};
      
      // 고정된 링크 목록을 기반으로 맵 생성
      fixedLinks.forEach((fixedLink) => {
        const existingLink = links.find((l) => l.linkId === fixedLink.linkId);
        linksMap[fixedLink.linkId] = {
          linkId: fixedLink.linkId,
          linkName: fixedLink.linkName,
          linkUrl: existingLink?.linkUrl || '',
          id: existingLink?.id,
          saved: !!existingLink, // 기존 링크가 있으면 저장됨
        };
      });
      
      setAttachments(linksMap);
    } catch (error) {
      console.error('Failed to load report data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSave = async () => {
    if (!selectedPeriod || !selectedCompany) {
      setMessage({ type: 'error', text: '기간과 회사를 선택해주세요.' });
      return;
    }

    // reviewer인 경우 저장하지 않고 팝업 표시
    if (user?.role === 'reviewer') {
      alert('demo를 위한 기능은 여기까지 제공됩니다. 실제 데이터는 저장되지 않습니다.');
      return;
    }

    setIsSaving(true);
    try {
      // 요약 저장
      await dlpService.saveSummary(selectedPeriod, selectedCompany, summaryContent);

      // 저장되지 않은 모든 링크 저장
      await saveAllUnsavedLinks();

      setMessage({ type: 'success', text: '저장되었습니다.' });
      // 데이터 다시 로드
      loadReportData();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || '저장에 실패했습니다.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  /** 작성완료 직전 — 화면의 요약과 저장 안 된 링크를 저장한다 (발행은 월 승인으로) */
  const saveBeforeWriteComplete = async () => {
    await dlpService.saveSummary(selectedPeriod, selectedCompany, summaryContent);
    await saveAllUnsavedLinks();
  };

  // 개별 링크 저장 (upsert)
  const handleSaveLink = async (linkId: string) => {
    if (!selectedPeriod || !selectedCompany) {
      setMessage({ type: 'error', text: '기간과 회사를 선택해주세요.' });
      return;
    }

    // reviewer인 경우 저장하지 않고 팝업 표시
    if (user?.role === 'reviewer') {
      alert('demo를 위한 기능은 여기까지 제공됩니다. 실제 데이터는 저장되지 않습니다.');
      return;
    }

    const attachment = attachments[linkId];
    if (!attachment || !attachment.linkUrl) {
      setMessage({ type: 'error', text: '링크 URL을 입력해주세요.' });
      return;
    }

    setSavingLinks({ ...savingLinks, [linkId]: true });
    try {
      if (attachment.id) {
        // 업데이트
        await dlpService.updateAttachment(attachment.id, {
          linkName: attachment.linkName,
          linkUrl: attachment.linkUrl,
          updateDate: new Date(),
        });
        // 업데이트 후 saved 상태 설정
        setAttachments({
          ...attachments,
          [linkId]: { ...attachment, saved: true },
        });
      } else {
        // 생성
        const created = await dlpService.createAttachment({
          period: selectedPeriod,
          companyName: selectedCompany,
          linkId: attachment.linkId,
          linkName: attachment.linkName,
          linkUrl: attachment.linkUrl,
          updateDate: new Date(),
        });
        // 생성된 ID 저장
        setAttachments({
          ...attachments,
          [linkId]: { ...attachment, id: created.id, saved: true },
        });
      }
      setMessage({ type: 'success', text: '링크가 저장되었습니다.' });
      // 데이터 다시 로드하지 않고 상태만 업데이트
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || '링크 저장에 실패했습니다.',
      });
    } finally {
      setSavingLinks({ ...savingLinks, [linkId]: false });
    }
  };

  const handleLinkUrlChange = (linkId: string, linkUrl: string) => {
    setAttachments({
      ...attachments,
      [linkId]: { ...attachments[linkId], linkUrl, saved: false }, // URL 변경 시 saved 상태 해제
    });
  };

  // 저장되지 않은 모든 링크 저장
  const saveAllUnsavedLinks = async () => {
    if (!selectedPeriod || !selectedCompany) {
      return;
    }

    const unsavedLinks = Object.values(attachments).filter(
      (attachment) => attachment.linkUrl && !attachment.saved
    );

    if (unsavedLinks.length === 0) {
      return; // 저장할 링크가 없음
    }

    for (const attachment of unsavedLinks) {
      const linkId = attachment.linkId;
      setSavingLinks({ ...savingLinks, [linkId]: true });
      try {
        if (attachment.id) {
          // 업데이트
          await dlpService.updateAttachment(attachment.id, {
            linkName: attachment.linkName,
            linkUrl: attachment.linkUrl,
            updateDate: new Date(),
          });
        } else {
          // 생성
          const created = await dlpService.createAttachment({
            period: selectedPeriod,
            companyName: selectedCompany,
            linkId: attachment.linkId,
            linkName: attachment.linkName,
            linkUrl: attachment.linkUrl,
            updateDate: new Date(),
          });
          // 생성된 ID 저장
          setAttachments({
            ...attachments,
            [linkId]: { ...attachment, id: created.id, saved: true },
          });
          continue; // 다음 링크로
        }
        // 업데이트 후 saved 상태 설정
        setAttachments({
          ...attachments,
          [linkId]: { ...attachment, saved: true },
        });
      } catch (error: any) {
        console.error(`링크 저장 실패 (${linkId}):`, error);
      } finally {
        setSavingLinks({ ...savingLinks, [linkId]: false });
      }
    }
  };

  return (
    <div>
      <h2>DLP 운영보고서 관리</h2>

      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
          <button onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      <div className="report-management-controls">
        <div className="control-group">
          <label>기간:</label>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            disabled={isLoading}
          >
            <option value="">선택하세요</option>
            {availablePeriods.map((period) => (
              <option key={period} value={period}>
                {period.slice(0, 4)}년 {period.slice(4)}월
              </option>
            ))}
          </select>
        </div>

        <div className="control-group">
          <label>회사:</label>
          <select
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.target.value)}
            disabled={isLoading}
          >
            <option value="">선택하세요</option>
            {availableCompanies.map((company) => (
              <option key={company.orgCode} value={company.companyName}>
                {company.companyName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedPeriod && selectedCompany && (
        <>
          <div className="summary-section" style={{ marginBottom: '3rem' }}>
            <h3>요약 작성</h3>
            <textarea
              className="summary-editor"
              value={summaryContent}
              onChange={(e) => setSummaryContent(e.target.value)}
              placeholder="Markdown 형식으로 요약을 작성하세요..."
              rows={10}
            />
            <button
              className="btn-primary"
              style={{ marginTop: '1rem' }}
              onClick={async () => {
                if (!selectedPeriod || !selectedCompany) {
                  setMessage({ type: 'error', text: '기간과 회사를 선택해주세요.' });
                  return;
                }
                if (user?.role === 'reviewer') {
                  alert('demo를 위한 기능은 여기까지 제공됩니다. 실제 데이터는 저장되지 않습니다.');
                  return;
                }
                setIsSaving(true);
                try {
                  await dlpService.saveSummary(selectedPeriod, selectedCompany, summaryContent);
                  setMessage({ type: 'success', text: '요약이 저장되었습니다.' });
                  loadReportData();
                } catch (error: any) {
                  setMessage({
                    type: 'error',
                    text: error.response?.data?.message || '저장에 실패했습니다.',
                  });
                } finally {
                  setIsSaving(false);
                }
              }}
              disabled={isSaving}
            >
              {isSaving ? '저장 중...' : '저장'}
            </button>
          </div>

          <div className="links-section" style={{ marginBottom: '3rem' }}>
            <h3>링크 입력</h3>
            <table className="links-table">
              <thead>
                <tr>
                  <th>링크명</th>
                  <th>링크 URL</th>
                  <th>작업</th>
                </tr>
              </thead>
              <tbody>
                {fixedLinks.map((fixedLink) => {
                  const attachment = attachments[fixedLink.linkId] || {
                    linkId: fixedLink.linkId,
                    linkName: fixedLink.linkName,
                    linkUrl: '',
                  };
                  const isSaving = savingLinks[fixedLink.linkId] || false;
                  
                  return (
                    <tr key={fixedLink.linkId}>
                      <td>{fixedLink.linkName}</td>
                      <td>
                        <input
                          type="text"
                          placeholder="링크 URL을 입력하세요"
                          value={attachment.linkUrl}
                          onChange={(e) => handleLinkUrlChange(fixedLink.linkId, e.target.value)}
                          className="link-url-input"
                          disabled={isSaving}
                        />
                      </td>
                      <td>
                        {attachment.saved ? (
                          <span className="link-saved-icon">✓</span>
                        ) : (
                          <button
                            className="btn-primary"
                            onClick={() => handleSaveLink(fixedLink.linkId)}
                            disabled={isSaving || savingLinks[fixedLink.linkId] || !selectedPeriod || !selectedCompany || !attachment.linkUrl}
                          >
                            {savingLinks[fixedLink.linkId] ? '저장 중...' : '저장'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* 기존 PUBLISH 자리 — 작성완료만 기록하고, 공개는 월 승인으로 (report-approval-flow SPEC D1) */}
          <WriteCompleteBar
            query={selectedPeriod && selectedCompany ? { pageType: 'DLP', period: selectedPeriod, companyName: selectedCompany } : null}
            role={user?.role}
            beforeComplete={saveBeforeWriteComplete}
          />
        </>
      )}
    </div>
  );
};

// DLP 라이센스 관리 섹션
const DlpLicenseManagementSection: React.FC = () => {
  const { user } = useAuth();
  const [licenses, setLicenses] = useState<DlpBuyLicense[]>([]);
  const [editedLicenses, setEditedLicenses] = useState<Record<string, string>>({});
  const [availableCompanies, setAvailableCompanies] = useState<CompanyInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // 엑셀 업로드
  const [uploadStatus, setUploadStatus] = useState<any>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<{ type: 'success' | 'error'; message: string; errors?: string[] } | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    loadData();
    loadUploadStatus();
  }, []);

  const loadUploadStatus = async () => {
    try {
      const status = await dlpService.getLatestUploadStatus();
      setUploadStatus(status);
    } catch (error) {
      console.error('Failed to load upload status:', error);
    }
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [licensesData, companiesData] = await Promise.all([
        dlpService.getAllLicenses(),
        companyService.getByPageType('DLP'),
      ]);
      setLicenses(licensesData);
      setAvailableCompanies(companiesData);
      
      // 초기 편집 상태 설정 (구매수량만)
      const initialEdited: Record<string, string> = {};
      licensesData.forEach((license) => {
        initialEdited[license.companyName] = license.amount.toString();
      });
      // availableCompanies에 있지만 licenses에 없는 회사는 빈 문자열로 초기화
      companiesData.forEach((company) => {
        if (!initialEdited[company.companyName]) {
          initialEdited[company.companyName] = '';
        }
      });
      setEditedLicenses(initialEdited);
    } catch (error) {
      console.error('Failed to load data:', error);
      setMessage({ type: 'error', text: '데이터를 불러오는데 실패했습니다.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAmountChange = (companyName: string, value: string) => {
    // 숫자와 쉼표만 허용
    const cleanedValue = value.replace(/[^0-9,]/g, '');
    setEditedLicenses((prev) => ({
      ...prev,
      [companyName]: cleanedValue,
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage(null);
    
    try {
      // 모든 회사에 대해 업데이트된 수량이 있으면 저장
      const savePromises: Promise<any>[] = [];
      
      for (const license of licenses) {
        const editedAmount = editedLicenses[license.companyName];
        if (editedAmount) {
          const amountNum = parseInt(editedAmount.replace(/,/g, ''), 10);
          if (!isNaN(amountNum) && amountNum !== license.amount) {
            // createLicense를 호출하면 before/after가 자동으로 계산됨
            const company = availableCompanies.find((c) => c.companyName === license.companyName);
            if (company) {
              savePromises.push(
                dlpService.createLicense({
                  orgCode: company.orgCode,
                  companyName: license.companyName,
                  amount: amountNum,
                  updateDate: new Date(),
                  updateUser: user?.email || 'system',
                })
              );
            }
          }
        }
      }

      // 새로 추가된 회사들 (licenses에 없지만 editedLicenses에 있는 경우)
      for (const company of availableCompanies) {
        if (!licenses.find((l) => l.companyName === company.companyName)) {
          const editedAmount = editedLicenses[company.companyName];
          if (editedAmount) {
            const amountNum = parseInt(editedAmount.replace(/,/g, ''), 10);
            if (!isNaN(amountNum) && amountNum > 0) {
              savePromises.push(
                dlpService.createLicense({
                  orgCode: company.orgCode,
                  companyName: company.companyName,
                  amount: amountNum,
                  updateDate: new Date(),
                  updateUser: user?.email || 'system',
                })
              );
            }
          }
        }
      }

      if (savePromises.length === 0) {
        setMessage({ type: 'error', text: '변경된 내용이 없습니다.' });
        setIsSaving(false);
        return;
      }

      await Promise.all(savePromises);
      await loadData();
      setMessage({ type: 'success', text: '라이센스 정보가 저장되었습니다.' });
    } catch (error: any) {
      console.error('Failed to save licenses:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || '라이센스 저장에 실패했습니다.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // 모든 회사 목록 (licenses에 있는 회사 + availableCompanies에 있는 회사)
  const allCompanies = React.useMemo(() => {
    const companyMap = new Map<string, CompanyInfo>();
    
    // availableCompanies를 먼저 추가
    availableCompanies.forEach((company) => {
      companyMap.set(company.companyName, company);
    });
    
    // licenses에 있는 회사도 추가 (availableCompanies에 없을 수 있음)
    licenses.forEach((license) => {
      if (!companyMap.has(license.companyName)) {
        companyMap.set(license.companyName, {
          orgCode: license.orgCode,
          companyName: license.companyName,
          dlpUseYn: 'Y',
          nacUseYn: 'N',
          aptUseYn: 'N',
        });
      }
    });
    
    return Array.from(companyMap.values())
      .filter((company) => company.companyName && company.companyName.trim() !== '')
      .sort((a, b) => {
        const nameA = a.companyName ?? '';
        const nameB = b.companyName ?? '';
        return nameA.localeCompare(nameB);
      });
  }, [licenses, availableCompanies]);

  return (
    <div>
      <h2 style={{ marginBottom: '1rem' }}>DLP 라이센스구매수량 관리</h2>

      {message && (
        <div className={`message ${message.type}`} style={{ maxWidth: '50%', marginBottom: '1rem' }}>
          {message.text}
          <button onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      {isLoading ? (
        <div className="loading">로딩 중...</div>
      ) : (
        <div style={{ maxWidth: '50%' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
            <button 
              className="btn-primary" 
              onClick={handleSave}
              disabled={isSaving || isLoading}
            >
              {isSaving ? '저장 중...' : '저장'}
            </button>
          </div>
          <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>회사</th>
                <th>구매수량</th>
              </tr>
            </thead>
            <tbody>
              {allCompanies.length === 0 ? (
                <tr>
                  <td colSpan={2} className="no-data">
                    등록된 회사가 없습니다.
                  </td>
                </tr>
              ) : (
                <>
                  {allCompanies.map((company) => {
                    const license = licenses.find((l) => l.companyName === company.companyName);
                    const currentAmount = editedLicenses[company.companyName] || 
                      (license ? license.amount.toString() : '');
                    
                    const isIrongrey = company.companyName === 'IRONGREY';
                    
                    return (
                      <React.Fragment key={company.companyName}>
                        <tr>
                          <td>{company.companyName}</td>
                          <td>
                            <input
                              type="text"
                              value={currentAmount}
                              onChange={(e) => handleAmountChange(company.companyName, e.target.value)}
                              className="license-amount-input"
                              placeholder="0"
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: '1px solid var(--card-border)',
                                borderRadius: '4px',
                                textAlign: 'right',
                              }}
                            />
                          </td>
                        </tr>
                        {/* IRONGREY 아래에 HPP 행 추가 */}
                        {isIrongrey && (
                          <tr>
                            <td>HPP</td>
                            <td>
                              <input
                                type="text"
                                value={editedLicenses['HPP'] || '4'}
                                onChange={(e) => handleAmountChange('HPP', e.target.value)}
                                className="license-amount-input"
                                placeholder="4"
                                style={{
                                  width: '100%',
                                  padding: '0.5rem',
                                  border: '1px solid var(--card-border)',
                                  borderRadius: '4px',
                                  textAlign: 'right',
                                }}
                              />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {/* 총합 행 */}
                  <tr style={{ fontWeight: 'bold', backgroundColor: 'var(--muted, #f5f5f5)' }}>
                    <td>총합</td>
                    <td>
                      {(
                        allCompanies.reduce((sum, company) => {
                          const license = licenses.find((l) => l.companyName === company.companyName);
                          const currentAmount = editedLicenses[company.companyName] || 
                            (license ? license.amount.toString() : '0');
                          const amountNum = parseInt(currentAmount.replace(/,/g, ''), 10) || 0;
                          return sum + amountNum;
                        }, 0) + 
                        (parseInt((editedLicenses['HPP'] || '4').replace(/,/g, ''), 10) || 4)
                      ).toLocaleString()}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* 엑셀 업로드 섹션 */}
      <div className="excel-upload-section" style={{ marginTop: '3rem' }}>
        <h3>엑셀 업로드</h3>
        
        {/* 업로드 상태 테이블 */}
        <div style={{ marginBottom: '2rem' }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>주제</th>
                <th>업로드유무</th>
                <th>업로드날짜</th>
                <th>작성자</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>라이선스 현황</td>
                <td>{uploadStatus?.licenseStat?.uploaded ? '✓' : '✗'}</td>
                <td>
                  {uploadStatus?.licenseStat?.uploadDate
                    ? new Date(uploadStatus.licenseStat.uploadDate).toLocaleString('ko-KR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      })
                    : '-'}
                </td>
                <td>{uploadStatus?.licenseStat?.register || '-'}</td>
              </tr>
              <tr>
                <td>퇴사자 라이선스 현황</td>
                <td>{uploadStatus?.exEmployee?.uploaded ? '✓' : '✗'}</td>
                <td>
                  {uploadStatus?.exEmployee?.uploadDate
                    ? new Date(uploadStatus.exEmployee.uploadDate).toLocaleString('ko-KR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      })
                    : '-'}
                </td>
                <td>{uploadStatus?.exEmployee?.register || '-'}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* 파일 선택 및 업로드 버튼 */}
        <div style={{ marginBottom: '2rem' }}>
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                setSelectedFile(file);
                setUploadResult(null);
              }
            }}
            style={{ marginBottom: '1rem' }}
            id="excel-file-input-license"
          />
          {selectedFile && (
            <div style={{ marginBottom: '1rem', padding: '0.5rem', backgroundColor: 'var(--muted, #f5f5f5)', borderRadius: '4px' }}>
              선택된 파일: {selectedFile.name}
            </div>
          )}
          <button
            className="btn-primary"
            onClick={async () => {
              if (!selectedFile) {
                setMessage({ type: 'error', text: '파일을 선택해주세요.' });
                return;
              }
              if (user?.role === 'reviewer') {
                alert('demo를 위한 기능은 여기까지 제공됩니다. 실제 데이터는 저장되지 않습니다.');
                return;
              }

              setIsUploading(true);
              setUploadResult(null);
              try {
                // 엑셀 파일에서 period와 company_name을 읽어오므로 빈 문자열 전달
                const result = await dlpService.uploadExcel(selectedFile, '', '');
                if (result.success) {
                  setUploadResult({ type: 'success', message: result.message });
                  setSelectedFile(null);
                  // 파일 input 초기화
                  const fileInput = document.getElementById('excel-file-input-license') as HTMLInputElement;
                  if (fileInput) fileInput.value = '';
                  // 업로드 상태 다시 로드
                  await loadUploadStatus();
                } else {
                  setUploadResult({
                    type: 'error',
                    message: result.message,
                    errors: result.errors,
                  });
                }
              } catch (error: any) {
                setUploadResult({
                  type: 'error',
                  message: error.response?.data?.message || '엑셀 업로드에 실패했습니다.',
                  errors: error.response?.data?.errors,
                });
              } finally {
                setIsUploading(false);
              }
            }}
            disabled={!selectedFile || isUploading}
          >
            {isUploading ? '업로드 중...' : selectedFile ? '실행' : '업로드'}
          </button>
        </div>

        {/* 실행 결과 메시지 영역 (실행 후에만 표시) */}
        {uploadResult && (
          <div
            style={{
              marginTop: '1rem',
              padding: '1rem',
              borderRadius: '4px',
              backgroundColor: uploadResult.type === 'success' ? '#d4edda' : '#f8d7da',
              color: uploadResult.type === 'success' ? '#155724' : '#721c24',
              border: `1px solid ${uploadResult.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
            }}
          >
            <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
              {uploadResult.type === 'success' ? '✓ 성공' : '✗ 오류'}
            </div>
            <div>{uploadResult.message}</div>
            {uploadResult.errors && uploadResult.errors.length > 0 && (
              <div style={{ marginTop: '0.5rem' }}>
                <div style={{ fontWeight: 'bold' }}>오류 상세:</div>
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                  {uploadResult.errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// 라이센스 추가 모달
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const LicenseAddModal: React.FC<{
  companies: CompanyInfo[];
  onClose: () => void;
  onSave: () => void;
}> = ({ companies, onClose, onSave }) => {
  const { user } = useAuth();
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>('');

  const handleSave = async () => {
    if (!selectedCompany) {
      setError('회사를 선택해주세요.');
      return;
    }

    const amountNum = parseInt(amount.replace(/,/g, ''), 10);
    if (isNaN(amountNum) || amountNum <= 0) {
      setError('구매수량을 올바르게 입력해주세요.');
      return;
    }

    setIsSaving(true);
    setError('');
    try {
      const company = companies.find((c) => c.companyName === selectedCompany);
      if (!company) {
        throw new Error('회사를 찾을 수 없습니다.');
      }

      await dlpService.createLicense({
        orgCode: company.orgCode,
        companyName: selectedCompany,
        amount: amountNum,
        updateDate: new Date(),
        updateUser: user?.email || 'system',
      });

      onSave();
    } catch (error: any) {
      setError(error.response?.data?.message || '라이센스 추가에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9,]/g, '');
    setAmount(value);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>라이센스 추가</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>회사:</label>
            <select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="form-input"
            >
              <option value="">선택하세요</option>
              {companies.map((company) => (
                <option key={company.orgCode} value={company.companyName}>
                  {company.companyName}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>구매수량:</label>
            <input
              type="text"
              value={amount}
              onChange={handleAmountChange}
              placeholder="예: 1,000"
              className="form-input"
            />
          </div>
          {error && <div className="error-message">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            취소
          </button>
          <button className="btn-primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
};

// 필터 없이 전체 조회 시 운영 데이터 수천 건이 한 번에 내려와 화면이 느려지던 문제(2026-09-17) —
// "보안 예외처리" 통합 탭과 동일하게 20건/페이지로 제한한다.
const DLP_EXCEPTION_PAGE_LIMIT = 20;

// DLP 예외처리 관리 섹션
export const DlpExceptionManagementSection: React.FC = () => {
  const [requests, setRequests] = useState<DlpExceptionRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(total / DLP_EXCEPTION_PAGE_LIMIT));
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingRequest, setEditingRequest] = useState<DlpExceptionRequest | null>(null);
  const [exceptionGroups, setExceptionGroups] = useState<string[]>([]);
  const [companies, setCompanies] = useState<CompanyInfo[]>([]);
  const [filters, setFilters] = useState({
    exceptGroup: '',
    reqCompany: '',
    search: '',
  });
  // IPT-624 — 정렬은 서버(DB orderBy)에 위임한다. 페이지당 20건만 내려오는데
  // 클라이언트에서 그 20건만 재정렬하면 전체 데이터 기준 정렬이 아니게 된다.
  const [sortKey, setSortKey] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    loadExceptionGroups();
    loadCompanies();
  }, []);

  useEffect(() => {
    setPage(1);
    loadData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    setPage(1);
    loadData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortKey, sortDir]);

  useEffect(() => {
    loadData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const loadData = async (targetPage = page) => {
    setIsLoading(true);
    try {
      const result = await dlpService.getExceptionRequests({
        ...filters,
        page: targetPage,
        limit: DLP_EXCEPTION_PAGE_LIMIT,
        sortKey: sortKey || undefined,
        sortDir,
      });
      setRequests(result.items);
      setTotal(result.total);
    } catch (error) {
      console.error('Failed to load exception requests:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 테이블 컬럼 정의 (IPT-562: ID·적용여부 제거, 허용IP·적용대상자·사유·예외기간·처리자·적용방법·비고 추가)
  const dlpColumns: { label: string; key?: keyof DlpExceptionRequest }[] = [
    { label: '예외 항목', key: 'exceptGroup' },
    { label: '예외 상세', key: 'exceptDetail' },
    { label: '신청자 계열사', key: 'reqCompany' },
    { label: '신청자 부서', key: 'reqDepartment' },
    { label: '신청자 이름', key: 'reqName' },
    { label: '대상자 계열사', key: 'targetCompany' },
    { label: '허용IP', key: 'permittedIp' },
    { label: '적용 대상자', key: 'permittedPc' },
    { label: '사유', key: 'reason' },
    { label: '기한', key: 'endDate' },
    { label: '처리자', key: 'actor' },
    { label: '예외 적용일', key: 'applyDateDisplay' },
    { label: '적용방법', key: 'applyMethod' },
    { label: '결재문서번호', key: 'approval' },
    { label: '비고', key: 'remark' },
    { label: '관리' },
  ];

  const loadExceptionGroups = async () => {
    try {
      const groups = await dlpService.getExceptionGroups();
      setExceptionGroups(groups);
    } catch (error) {
      console.error('Failed to load exception groups:', error);
    }
  };

  const loadCompanies = async () => {
    try {
      const data = await companyService.getByPageType('DLP');
      setCompanies(data);
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  const handleCreate = () => {
    setEditingRequest(null);
    setShowModal(true);
  };

  const handleEdit = (request: DlpExceptionRequest) => {
    setEditingRequest(request);
    setShowModal(true);
  };

  // 복제 — id를 들고 가지 않아 DlpExceptionForm이 수정이 아니라 신규 등록으로 처리한다
  const handleDuplicate = (request: DlpExceptionRequest) => {
    setEditingRequest({ ...request, id: undefined });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('정말로 이 예외처리 요청을 삭제하시겠습니까?')) {
      return;
    }

    try {
      await dlpService.deleteExceptionRequest(id);
      loadData();
    } catch (error) {
      console.error('Failed to delete exception request:', error);
      alert('삭제에 실패했습니다.');
    }
  };

  // CSV/Excel 업로드 결과 메시지
  const [uploadExcelResult, setUploadExcelResult] = useState<{ type: 'success' | 'error'; message: string; errors?: string[] } | null>(null);
  const { user } = useAuth();

  const handleUploadDlp = async (file: File) => {
    if (user?.role === 'reviewer') {
      alert('demo를 위한 기능은 여기까지 제공됩니다. 실제 데이터는 저장되지 않습니다.');
      throw new Error('reviewer-blocked'); // 파일 유지
    }
    setUploadExcelResult(null);
    try {
      const result = await dlpService.uploadExceptionRequestsExcel(file);
      if (!result.success) {
        setUploadExcelResult({ type: 'error', message: result.message, errors: result.errors });
        throw new Error('upload-failed'); // 파일 유지
      }
      setUploadExcelResult({ type: 'success', message: result.message });
      await loadData();
    } catch (error: any) {
      if (error?.message !== 'upload-failed') {
        // IPT-613 — 처리자 값 오류는 저장 전에 파일 전체가 거부된다. 걸린 행을 그대로 보여준다.
        const rejection = toActorRejection(error);
        setUploadExcelResult({
          type: 'error',
          message: rejection?.message || error?.response?.data?.message || '업로드에 실패했습니다.',
          errors: rejection
            ? [
                ...rejection.invalidActors.map((r) => `${r.row}행 — '${r.value}'`),
                ...(rejection.allowedActors.length
                  ? [`사용 가능: ${rejection.allowedActors.join(' · ')}`]
                  : []),
              ]
            : undefined,
        });
      }
      throw error;
    }
  };

  return (
    <div>
      <ExHeader
        title="DLP 예외처리 관리"
        total={total}
        onAdd={handleCreate}
        addLabel="+ 예외처리 추가"
        description="데이터 유출방지(DLP) 예외처리 신청 및 관리"
      />

      {/* 벌크 업로드 (CSV/Excel) */}
      <div style={{ marginBottom: '1.25rem' }}>
        <BulkUploadBar sample={EXCEPTION_SAMPLE_CSV.dlp} onUpload={handleUploadDlp} />
        {uploadExcelResult && (
          <div
            style={{
              marginTop: '0.5rem', padding: '0.6rem 1rem', borderRadius: '6px',
              backgroundColor: uploadExcelResult.type === 'success' ? '#f0fdf4' : '#fef2f2',
              color: uploadExcelResult.type === 'success' ? '#16a34a' : '#dc2626',
              border: `1px solid ${uploadExcelResult.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
              fontSize: '0.82rem',
            }}
          >
            <strong>{uploadExcelResult.type === 'success' ? '✓ 성공' : '✗ 오류'}</strong> {uploadExcelResult.message}
            {uploadExcelResult.errors && uploadExcelResult.errors.length > 0 && (
              <ul style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                {uploadExcelResult.errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* 검색 바 — IPT-619: 신청자 계열사가 예외 항목 구성을 가르는 기준이라 맨 앞으로 */}
      <ExSearchBar>
        <UiSelect value={filters.reqCompany} onChange={(e) => setFilters({ ...filters, reqCompany: e.target.value })} className="w-auto min-w-[150px]">
          <option value="">신청자 계열사 (전체)</option>
          {companies.map((company) => <option key={company.orgCode} value={company.companyName}>{company.companyName}</option>)}
        </UiSelect>
        <UiSelect value={filters.exceptGroup} onChange={(e) => setFilters({ ...filters, exceptGroup: e.target.value })} className="w-auto min-w-[150px]">
          <option value="">예외 항목 (전체)</option>
          {exceptionGroups.map((group) => <option key={group} value={group}>{group}</option>)}
        </UiSelect>
        <UiInput type="text" placeholder="검색 (이름, 부서, 사유, 대상자 계열사)" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className="w-auto min-w-[220px] flex-1" />
      </ExSearchBar>

      {/* 등록/수정 인라인 카드 */}
      {showModal && (
        <DlpExceptionForm
          request={editingRequest}
          exceptionGroups={exceptionGroups}
          companies={companies}
          onClose={() => { setShowModal(false); setEditingRequest(null); }}
          onSave={() => { setShowModal(false); setEditingRequest(null); loadData(); }}
        />
      )}

      {/* 목록 테이블 */}
      {isLoading ? (
        <ExLoading />
      ) : requests.length === 0 ? (
        <ExEmpty text="데이터가 없습니다." />
      ) : (
        <ExTableShell
          headers={
            <>
              {dlpColumns.map((col) =>
                col.key ? (
                  <ExSortableTh
                    key={col.label}
                    label={col.label}
                    sortKey={col.key as string}
                    activeKey={sortKey || undefined}
                    dir={sortDir}
                    onSort={handleSort}
                  />
                ) : (
                  <th key={col.label} style={exTh}>{col.label}</th>
                ),
              )}
            </>
          }
        >
          {requests.map((request, idx) => (
            <tr key={request.id} style={{ background: exBg(idx), borderBottom: '1px solid var(--border, #e5e7eb)' }}>
              <td style={exTd}>{request.exceptGroup || '-'}</td>
              <td style={exTd}>{request.exceptDetailEnum?.itemName || request.exceptDetail || '-'}</td>
              <td style={{ ...exTd, fontWeight: 500 }}>{request.reqCompany || '-'}</td>
              <td style={exTd}>{request.reqDepartment || '-'}</td>
              <td style={exTd}>{request.reqName || '-'}</td>
              <td style={exTd}>{request.targetCompany || '-'}</td>
              <td style={{ ...exTd, fontFamily: 'monospace' }}>{request.permittedIp || '-'}</td>
              <td style={exTd}>{request.permittedPc || '-'}</td>
              <td style={{ ...exTd, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{request.reason || '-'}</td>
              <td style={{ ...exTd, whiteSpace: 'nowrap' }}>{request.endDate ? String(request.endDate).slice(0, 10) : '영구'}</td>
              <td style={exTd}>{request.actor || '-'}</td>
              <td style={{ ...exTd, whiteSpace: 'nowrap' }}>{request.applyDateDisplay || '-'}</td>
              <td style={exTd}>{request.applyMethod || '-'}</td>
              <td style={{ ...exTd, whiteSpace: 'nowrap' }}>{request.approval || '-'}</td>
              <td style={{ ...exTd, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{request.remark || '-'}</td>
              <td style={{ ...exTd, whiteSpace: 'nowrap' }}>
                <ExRowActions
                  onEdit={() => handleEdit(request)}
                  onDelete={() => handleDelete(request.id!)}
                  onDuplicate={() => handleDuplicate(request)}
                />
              </td>
            </tr>
          ))}
        </ExTableShell>
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', padding: '0.75rem' }}>
          <button onClick={() => setPage(1)} disabled={page === 1} style={{ padding: '0.3rem 0.6rem', borderRadius: '5px', border: '1.5px solid var(--border, #d1d5db)', background: 'transparent', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1, fontSize: '0.8rem' }}>«</button>
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: '0.3rem 0.6rem', borderRadius: '5px', border: '1.5px solid var(--border, #d1d5db)', background: 'transparent', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1, fontSize: '0.8rem' }}>‹</button>
          <span style={{ fontSize: '0.82rem', fontWeight: 500, padding: '0 0.5rem', color: 'var(--foreground, #111)' }}>{page} / {totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ padding: '0.3rem 0.6rem', borderRadius: '5px', border: '1.5px solid var(--border, #d1d5db)', background: 'transparent', cursor: page === totalPages ? 'not-allowed' : 'pointer', opacity: page === totalPages ? 0.4 : 1, fontSize: '0.8rem' }}>›</button>
          <button onClick={() => setPage(totalPages)} disabled={page === totalPages} style={{ padding: '0.3rem 0.6rem', borderRadius: '5px', border: '1.5px solid var(--border, #d1d5db)', background: 'transparent', cursor: page === totalPages ? 'not-allowed' : 'pointer', opacity: page === totalPages ? 0.4 : 1, fontSize: '0.8rem' }}>»</button>
        </div>
      )}
    </div>
  );
};

// 예외처리 요청 인라인 폼
const DlpExceptionForm: React.FC<{
  request: DlpExceptionRequest | null;
  exceptionGroups: string[];
  companies: CompanyInfo[];
  onClose: () => void;
  onSave: () => void;
}> = ({ request, exceptionGroups, companies, onClose, onSave }) => {
  const [formData, setFormData] = useState<Partial<DlpExceptionRequest>>({
    exceptGroup: request?.exceptGroup || '',
    exceptDetail: request?.exceptDetail || '',
    reqCompany: request?.reqCompany || '',
    reqDepartment: request?.reqDepartment || '',
    reqName: request?.reqName || '',
    targetCompany: request?.targetCompany || '',
    permittedIp: request?.permittedIp || '',
    permittedPc: request?.permittedPc || '',
    reason: request?.reason || '',
    period: request?.period || '',
    approval: request?.approval || '',
    actor: request?.actor || '',
    applyDate: request?.applyDate ? String(request.applyDate).slice(0, 10) : '',
    endDate: request?.endDate ? String(request.endDate).slice(0, 10) : '',
    applyMethod: request?.applyMethod || '',
    remark: request?.remark || '',
  });
  const [selectedGroup, setSelectedGroup] = useState(request?.exceptGroup || '');
  const [availableDetails, setAvailableDetails] = useState<any[]>([]);
  // IPT-619 — 예외 항목 목록은 신청자 계열사(홀딩스/제강)에 따라 갈린다. 부모의
  // exceptionGroups(필터용, 홀딩스 기본값)를 초기값으로 쓰고 신청자 계열사가
  // 정해지면 그 계열사군에 맞는 목록으로 다시 불러온다.
  const [groupOptions, setGroupOptions] = useState<string[]>(exceptionGroups);
  // IPT-613 — 기한 '영구' 체크 상태. 신규 등록은 해제로 시작(NAC·유해차단과 동일),
  // 기존 행은 기한이 비어 있으면 영구로 열린다.
  const [isPermanent, setIsPermanent] = useState(Boolean(request) && !request?.endDate);
  // 처리자 후보는 DB 마스터에서 받아온다 (관리자 화면에서 추가·삭제)
  const [actorOptions, setActorOptions] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    exceptionActorService
      .getAll()
      .then((list) => setActorOptions(list.map((a) => a.name)))
      .catch(() => setActorOptions([]));
  }, []);

  // IPT-619 — 신청자 계열사가 바뀔 때마다 그 계열사군의 예외 항목 목록을 다시 불러온다.
  // 최초 마운트(수정 화면)에서도 한 번 돌아 기존 값과 계열사군이 맞는지 맞춰준다.
  useEffect(() => {
    let cancelled = false;
    dlpService
      .getExceptionGroups(formData.reqCompany || undefined)
      .then((groups) => {
        if (!cancelled) setGroupOptions(groups);
      })
      .catch(() => {
        if (!cancelled) setGroupOptions([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.reqCompany]);

  useEffect(() => {
    if (selectedGroup) {
      loadDetails(selectedGroup, formData.reqCompany);
    } else {
      setAvailableDetails([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup, formData.reqCompany]);

  const loadDetails = async (group: string, reqCompany?: string) => {
    try {
      const details = await dlpService.getExceptionDetails(group, reqCompany);
      setAvailableDetails(details);
    } catch (error) {
      console.error('Failed to load exception details:', error);
    }
  };

  const handleGroupChange = (group: string) => {
    setSelectedGroup(group);
    setFormData({ ...formData, exceptGroup: group, exceptDetail: '' });
  };

  // IPT-619 — 계열사군이 바뀌면 예외 항목 구성 자체가 달라지므로, 이미 골라둔
  // 예외 항목/상세를 그대로 두면 다른 계열사군의 값이 저장될 수 있다. 초기화한다.
  const handleReqCompanyChange = (reqCompany: string) => {
    setSelectedGroup('');
    setFormData({ ...formData, reqCompany, exceptGroup: '', exceptDetail: '' });
  };

  const handleSave = async () => {
    // IPT-613 — 목록 밖 처리자는 저장하지 않는다 (그대로 저장하면 값이 지워진다)
    if (!isAllowedActor(formData.actor, actorOptions)) return;
    // IPT-624 — 결재문서번호·처리방법은 필수 입력 (원복 시 근거·절차 추적용)
    if (!formData.approval?.trim() || !formData.applyMethod?.trim()) {
      alert('결재문서번호와 처리방법은 필수 입력 항목입니다.');
      return;
    }

    setIsSaving(true);
    try {
      // 영구면 기한을 비워 보낸다 — 서버는 빈 문자열을 기한 없음(영구)으로 저장한다 (IPT-562 D)
      const payload = { ...formData, endDate: isPermanent ? '' : formData.endDate };
      if (request?.id) {
        await dlpService.updateExceptionRequest(request.id, payload);
      } else {
        await dlpService.createExceptionRequest(payload);
      }
      onSave();
    } catch (error) {
      console.error('Failed to save exception request:', error);
      alert('저장에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ExFormCard
      title={request?.id ? 'DLP 예외처리 수정' : 'DLP 예외처리 등록'}
      onClose={onClose}
      onSave={handleSave}
      isSaving={isSaving}
      saveLabel={request?.id ? '수정 저장' : '추가'}
    >
      {/* 목록 밖 처리자 — 어떤 값이 걸렸는지 보여주고 저장을 막는다 */}
      {!isAllowedActor(formData.actor, actorOptions) && (
        <div
          role="alert"
          style={{
            marginBottom: '1rem', padding: '0.75rem 1rem', borderRadius: '8px',
            border: '1px solid #fcd34d', background: '#fffbeb', color: '#78350f',
            fontSize: '0.85rem', wordBreak: 'keep-all',
          }}
        >
          저장된 처리자 <strong>‘{formData.actor}’</strong>은(는) 선택할 수 있는 값이 아닙니다.
          아래에서 처리자를 다시 고른 뒤 저장해 주세요.
        </div>
      )}

      <ExFieldset legend="예외 항목" accent>
        <ExField label="신청자 계열사 *" htmlFor="dlp-req-company">
          <select id="dlp-req-company" value={formData.reqCompany} onChange={(e) => handleReqCompanyChange(e.target.value)} style={exFld}>
            <option value="">선택하세요</option>
            {companies.map((company) => <option key={company.orgCode} value={company.companyName}>{company.companyName}</option>)}
          </select>
        </ExField>
        <ExField label="예외 항목 *" htmlFor="dlp-except-group">
          <select id="dlp-except-group" value={selectedGroup} onChange={(e) => handleGroupChange(e.target.value)} style={exFld}>
            <option value="">선택하세요</option>
            {groupOptions.map((group) => <option key={group} value={group}>{group}</option>)}
          </select>
        </ExField>
        <ExField label="예외 상세 *" htmlFor="dlp-except-detail">
          <select id="dlp-except-detail" value={formData.exceptDetail} onChange={(e) => setFormData({ ...formData, exceptDetail: e.target.value })} style={{ ...exFld, opacity: !selectedGroup ? 0.5 : 1 }} disabled={!selectedGroup}>
            <option value="">선택하세요</option>
            {availableDetails.map((detail) => <option key={detail.itemCode} value={detail.itemName}>{detail.itemName}</option>)}
          </select>
        </ExField>
        <ExField label="대상자 계열사" htmlFor="dlp-target-company">
          <select id="dlp-target-company" value={formData.targetCompany} onChange={(e) => setFormData({ ...formData, targetCompany: e.target.value })} style={exFld}>
            <option value="">선택하세요</option>
            {companies.map((company) => <option key={company.orgCode} value={company.companyName}>{company.companyName}</option>)}
          </select>
        </ExField>
      </ExFieldset>

      <ExFieldset legend="신청자 정보">
        <ExField label="신청자 부서">
          <input type="text" value={formData.reqDepartment} onChange={(e) => setFormData({ ...formData, reqDepartment: e.target.value })} placeholder="부서 입력" style={exFld} />
        </ExField>
        <ExField label="신청자 이름">
          <input type="text" value={formData.reqName} onChange={(e) => setFormData({ ...formData, reqName: e.target.value })} placeholder="이름 입력" style={exFld} />
        </ExField>
        <ExField label="처리자" htmlFor="dlp-actor">
          <select
            id="dlp-actor"
            value={formData.actor || ''}
            onChange={(e) => setFormData({ ...formData, actor: e.target.value })}
            style={exFld}
          >
            <option value="">선택하세요</option>
            {actorOptions.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </ExField>
        <ExField label="결재문서번호" htmlFor="dlp-approval">
          <input id="dlp-approval" type="text" value={formData.approval || ''} onChange={(e) => setFormData({ ...formData, approval: e.target.value })} placeholder="결재문서번호 입력" style={exFld} />
        </ExField>
      </ExFieldset>

      <ExFieldset legend="접근 대상">
        <ExField label="허용 PC IP 정보">
          <input type="text" value={formData.permittedIp} onChange={(e) => setFormData({ ...formData, permittedIp: e.target.value })} placeholder="예: 192.168.1.100" style={{ ...exFld, fontFamily: 'monospace' }} />
        </ExField>
        <ExField label="허용PC 사용자/계정">
          <input type="text" value={formData.permittedPc} onChange={(e) => setFormData({ ...formData, permittedPc: e.target.value })} placeholder="사용자명/계정 입력" style={exFld} />
        </ExField>
      </ExFieldset>

      <ExFieldset legend="처리 기간" minColWidth={180}>
        <ExField label="예외 적용일" htmlFor="dlp-apply-date">
          <input id="dlp-apply-date" type="date" value={formData.applyDate} onChange={(e) => setFormData({ ...formData, applyDate: e.target.value })} style={exFld} />
        </ExField>
        <ExField label="기한" htmlFor="dlp-end-date">
          {/* IPT-613 — NAC과 같은 '영구' 체크박스. 저장 결과는 종전과 같다(기한 없음 = 영구) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              id="dlp-end-date"
              type="date"
              value={formData.endDate || ''}
              onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
              disabled={isPermanent}
              style={{ ...exFld, flex: 1, opacity: isPermanent ? 0.5 : 1 }}
            />
            <label htmlFor="dlp-permanent" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem', whiteSpace: 'nowrap', cursor: 'pointer' }}>
              <input
                id="dlp-permanent"
                type="checkbox"
                checked={isPermanent}
                onChange={(e) => {
                  setIsPermanent(e.target.checked);
                  if (e.target.checked) setFormData((prev) => ({ ...prev, endDate: '' }));
                }}
              />
              영구
            </label>
          </div>
        </ExField>
        <ExField label="처리방법" htmlFor="dlp-apply-method" className="col-span-2">
          <input id="dlp-apply-method" type="text" value={formData.applyMethod} onChange={(e) => setFormData({ ...formData, applyMethod: e.target.value })} placeholder="최대한 구체적으로 작성 (원복 시 활용 필요)" style={exFld} />
        </ExField>
      </ExFieldset>

      <ExFieldset legend="상세 내용" columns="1fr 1fr">
        <ExField label="사유">
          <textarea value={formData.reason} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} rows={3} style={{ ...exFld, resize: 'vertical' }} />
        </ExField>
        <ExField label="비고">
          <textarea value={formData.remark} onChange={(e) => setFormData({ ...formData, remark: e.target.value })} rows={3} style={{ ...exFld, resize: 'vertical' }} />
        </ExField>
      </ExFieldset>
    </ExFormCard>
  );
};

// NAC 운영보고서 관리 섹션
export const NacReportManagementSection: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [availablePeriods, setAvailablePeriods] = useState<string[]>([]);
  const [availableCompanies, setAvailableCompanies] = useState<CompanyInfo[]>([]);
  const [summaryContent, setSummaryContent] = useState<string>('');

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  const { user } = useAuth();

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedPeriod && selectedCompany) {
      loadReportData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod, selectedCompany]);

  const loadInitialData = async () => {
    setIsLoading(true);
    try {
      const periods = await nacService.getAvailablePeriods();
      setAvailablePeriods(periods);
      if (periods.length > 0) {
        setSelectedPeriod(periods[0]);
      }

      const companies = await companyService.getByPageType('NAC');
      setAvailableCompanies(companies);
      if (companies.length === 1) {
        setSelectedCompany(companies[0].companyName);
      }
    } catch (error) {
      console.error('Failed to load initial data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadReportData = async () => {
    if (!selectedPeriod || !selectedCompany) return;

    setIsLoading(true);
    try {
      // 요약 내용 로드
      const summary = await nacService.getSummary(selectedPeriod, selectedCompany);
      setSummaryContent(summary?.summaryContent || '');
    } catch (error) {
      console.error('Failed to load report data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  /** 작성완료 직전 — 화면의 요약을 저장한다 (발행은 월 승인으로) */
  const saveBeforeWriteComplete = async () => {
    await nacService.saveSummary(selectedPeriod, selectedCompany, summaryContent);
  };

  return (
    <div>
      <h2>NAC 운영보고서 관리</h2>

      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
          <button onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      <div className="report-management-controls">
        <div className="control-group">
          <label>기간:</label>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            disabled={isLoading}
          >
            <option value="">선택하세요</option>
            {availablePeriods.map((period) => (
              <option key={period} value={period}>
                {period.slice(0, 4)}년 {period.slice(4)}월
              </option>
            ))}
          </select>
        </div>

        <div className="control-group">
          <label>회사:</label>
          <select
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.target.value)}
            disabled={isLoading}
          >
            <option value="">선택하세요</option>
            {availableCompanies.map((company) => (
              <option key={company.orgCode} value={company.companyName}>
                {company.companyName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedPeriod && selectedCompany && (
        <>
          <div className="summary-section" style={{ marginBottom: '3rem' }}>
            <h3>요약 작성</h3>
            <textarea
              className="summary-editor"
              value={summaryContent}
              onChange={(e) => setSummaryContent(e.target.value)}
              placeholder="Markdown 형식으로 요약을 작성하세요..."
              rows={10}
            />
            <button
              className="btn-primary"
              style={{ marginTop: '1rem' }}
              onClick={async () => {
                if (!selectedPeriod || !selectedCompany) {
                  setMessage({ type: 'error', text: '기간과 회사를 선택해주세요.' });
                  return;
                }
                if (user?.role === 'reviewer') {
                  alert('demo를 위한 기능은 여기까지 제공됩니다. 실제 데이터는 저장되지 않습니다.');
                  return;
                }
                setIsSaving(true);
                try {
                  await nacService.saveSummary(selectedPeriod, selectedCompany, summaryContent);
                  setMessage({ type: 'success', text: '요약이 저장되었습니다.' });
                  loadReportData();
                } catch (error: any) {
                  setMessage({
                    type: 'error',
                    text: error.response?.data?.message || '저장에 실패했습니다.',
                  });
                } finally {
                  setIsSaving(false);
                }
              }}
              disabled={isSaving}
            >
              {isSaving ? '저장 중...' : '저장'}
            </button>
          </div>

          {/* 기존 PUBLISH 자리 — 작성완료만 기록하고, 공개는 월 승인으로 (report-approval-flow SPEC D1) */}
          <WriteCompleteBar
            query={selectedPeriod && selectedCompany ? { pageType: 'NAC', period: selectedPeriod, companyName: selectedCompany } : null}
            role={user?.role}
            beforeComplete={saveBeforeWriteComplete}
          />
        </>
      )}
    </div>
  );
};

// NAC 라이센스 관리 섹션
// APT 데이터 업로드 섹션
const AptDataUploadSection: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [deleteExisting, setDeleteExisting] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [round, setRound] = useState<string>('');
  const [period, setPeriod] = useState<string>('');
  const [availableRounds, setAvailableRounds] = useState<string[]>([]);
  const [availablePeriods, setAvailablePeriods] = useState<string[]>([]);

  useEffect(() => {
    // 사용 가능한 round와 period 목록 로드
    const loadOptions = async () => {
      try {
        const [rounds, periods] = await Promise.all([
          resultsService.getAvailableRounds(),
          resultsService.getAvailablePeriods(),
        ]);
        setAvailableRounds(rounds);
        setAvailablePeriods(periods);
        if (rounds.length > 0) setRound(rounds[0]); // 기본값 설정
        if (periods.length > 0) setPeriod(periods[0]); // 기본값 설정
      } catch (err) {
        console.error('옵션 로드 실패:', err);
      }
    };
    loadOptions();
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // 확장자 체크 (대소문자 무관)
      const fileName = file.name.toLowerCase();
      if (!fileName.endsWith('.csv')) {
        setError('CSV 파일만 업로드 가능합니다.');
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      setError(null);
      setUploadResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('CSV 파일을 선택해주세요.');
      return;
    }

    if (!round || round.trim() === '') {
      setError('회차(round)를 입력해주세요.');
      return;
    }

    if (!period || period.trim() === '') {
      setError('기간(period)을 입력해주세요.');
      return;
    }

    // Period 형식 검증 (YYYYMM)
    if (!/^\d{6}$/.test(period)) {
      setError('기간(period)은 YYYYMM 형식이어야 합니다. (예: 202512)');
      return;
    }

    // 기존 데이터 삭제 확인
    if (deleteExisting) {
      const confirmed = window.confirm(
        `⚠️ 경고: round="${round}", period="${period}"의 기존 APT 데이터를 모두 삭제하고 새 데이터를 삽입합니다.\n\n정말로 진행하시겠습니까?`
      );
      if (!confirmed) {
        return;
      }
    }

    setIsUploading(true);
    setError(null);
    setUploadResult(null);

    try {
      const result = await resultsService.uploadAptDataCsv(selectedFile, round, period, deleteExisting);
      setUploadResult(result);
      setSelectedFile(null);
      setDeleteExisting(false); // 업로드 후 체크박스 초기화
      // 파일 input 초기화
      const fileInput = document.getElementById('apt-data-file-input') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (err: any) {
      console.error('❌ 업로드 실패:', err);
      console.error('❌ 에러 상세:', err.response?.data);
      
      let errorMessage = 'CSV 파일 업로드에 실패했습니다.';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div>
      <h2>악성메일 모의 훈련 열람결과 입력</h2>
      <div style={{ marginBottom: '2rem' }}>
        <p style={{ marginBottom: '1rem', color: 'var(--muted-foreground, #666)' }}>
          CSV 파일을 업로드하여 APT 데이터(is_apt_data_result)를 삽입합니다.
        </p>
        <div style={{ 
          padding: '1rem', 
          backgroundColor: '#fff3cd', 
          border: '1px solid #ffc107',
          borderRadius: '4px',
          marginBottom: '1rem'
        }}>
          <p style={{ margin: 0, color: '#856404', fontSize: '0.9rem' }}>
            ⚠️ <strong>중요:</strong> 회차(round)와 기간(period)을 먼저 선택해야 합니다.<br/>
            ℹ️ 일반적으로는 기존 데이터에 추가(append)됩니다. "기존 데이터 삭제" 옵션은 선택한 round/period의 데이터 전체 재구축 시에만 사용하세요.
          </p>
        </div>
        <p style={{ marginBottom: '1rem', color: 'var(--muted-foreground, #666)', fontSize: '0.9rem' }}>
          <strong>CSV 파일 형식:</strong> 회사, 팀, 이름, 이메일, Reported
        </p>
        <p style={{ marginBottom: '0.5rem', color: 'var(--muted-foreground, #666)', fontSize: '0.9rem' }}>
          <strong>필수 컬럼:</strong>
        </p>
        <ul style={{ color: 'var(--muted-foreground, #666)', fontSize: '0.9rem', marginLeft: '1.5rem', marginBottom: '1rem' }}>
          <li>회사 (company)</li>
          <li>팀 (team)</li>
          <li>이름 (name) - 자동 암호화</li>
          <li>이메일 (email) - 자동 암호화</li>
          <li>Reported (result) - 0=미열람, 1=메일열람, 2=링크클릭, 3=정보입력</li>
        </ul>
        <p style={{ marginBottom: '0rem', color: 'var(--muted-foreground, #999)', fontSize: '0.85rem' }}>
          ※ 회차(round)와 기간(period)은 UI에서 선택하므로 CSV에 포함하지 않아도 됩니다.
        </p>
      </div>

      <div style={{ 
        background: 'var(--card-bg)', 
        padding: '2rem', 
        borderRadius: '8px', 
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        marginBottom: '2rem'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
          <div>
            <label htmlFor="apt-round-input" style={{ 
              display: 'block', 
              marginBottom: '0.5rem', 
              fontWeight: 500,
              fontSize: '1rem'
            }}>
              <span style={{ color: 'red' }}>*</span> 회차 (Round):
            </label>
            <input
              id="apt-round-input"
              list="apt-rounds-list"
              value={round}
              onChange={(e) => setRound(e.target.value)}
              disabled={isUploading}
              placeholder="선택 또는 직접 입력 (예: seah_3)"
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--card-border)',
                borderRadius: '4px',
                fontSize: '1rem',
              }}
            />
            <datalist id="apt-rounds-list">
              {availableRounds.map((r) => (
                <option key={r} value={r} />
              ))}
            </datalist>
            <p style={{ marginTop: '0.3rem', color: 'var(--muted-foreground, #666)', fontSize: '0.85rem' }}>
              💡 목록에서 선택하거나 새로운 회차를 직접 입력하세요
            </p>
          </div>

          <div>
            <label htmlFor="apt-period-input" style={{ 
              display: 'block', 
              marginBottom: '0.5rem', 
              fontWeight: 500,
              fontSize: '1rem'
            }}>
              <span style={{ color: 'red' }}>*</span> 기간 (Period):
            </label>
            <input
              id="apt-period-input"
              list="apt-periods-list"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              disabled={isUploading}
              placeholder="선택 또는 직접 입력 (예: 202512)"
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--card-border)',
                borderRadius: '4px',
                fontSize: '1rem',
              }}
            />
            <datalist id="apt-periods-list">
              {availablePeriods.map((p) => (
                <option key={p} value={p} />
              ))}
            </datalist>
            <p style={{ marginTop: '0.3rem', color: 'var(--muted-foreground, #666)', fontSize: '0.85rem' }}>
              💡 YYYYMM 형식 (예: 202512)
            </p>
          </div>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label htmlFor="apt-data-file-input" style={{ 
            display: 'block', 
            marginBottom: '0.5rem', 
            fontWeight: 500,
            fontSize: '1rem'
          }}>
            CSV 파일 선택:
          </label>
          <input
            id="apt-data-file-input"
            type="file"
            accept=".csv,.CSV"
            onChange={handleFileChange}
            disabled={isUploading}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid var(--card-border)',
              borderRadius: '4px',
              fontSize: '1rem',
            }}
          />
          {selectedFile && (
            <p style={{ marginTop: '0.5rem', color: '#28a745', fontSize: '0.9rem' }}>
              선택된 파일: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
            </p>
          )}
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ 
            display: 'flex', 
            alignItems: 'center',
            cursor: 'pointer',
            fontSize: '1rem'
          }}>
            <input
              type="checkbox"
              checked={deleteExisting}
              onChange={(e) => setDeleteExisting(e.target.checked)}
              disabled={isUploading}
              style={{
                marginRight: '0.5rem',
                width: '18px',
                height: '18px',
                cursor: 'pointer',
              }}
            />
            <span style={{ color: deleteExisting ? 'var(--danger)' : 'var(--content-text)' }}>
              선택한 round/period의 기존 데이터 삭제 후 삽입 (전체 재구축 시에만 사용)
            </span>
          </label>
          {deleteExisting && (
            <p style={{ 
              marginTop: '0.5rem', 
              marginLeft: '1.5rem',
              color: 'var(--danger)', 
              fontSize: '0.85rem',
              fontWeight: 500 
            }}>
              ⚠️ 주의: 기존의 모든 APT 데이터가 삭제됩니다! 일반적인 경우에는 이 옵션을 사용하지 마세요.
            </p>
          )}
        </div>

        {error && (
          <div style={{
            padding: '1rem',
            marginBottom: '1rem',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
          }}>
            {error}
          </div>
        )}

        <button
          onClick={handleUpload}
          disabled={!selectedFile || isUploading}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: selectedFile && !isUploading ? '#007bff' : '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: selectedFile && !isUploading ? 'pointer' : 'not-allowed',
            fontSize: '1rem',
            fontWeight: 500,
          }}
        >
          {isUploading ? '업로드 중...' : 'CSV 파일 업로드'}
        </button>
      </div>

      {uploadResult && (
        <div style={{
          background: 'var(--card-bg)',
          padding: '2rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        }}>
          <h3 style={{ marginBottom: '1rem', color: uploadResult.success ? '#28a745' : '#dc3545' }}>
            {uploadResult.success ? '✅ 업로드 성공!' : '❌ 업로드 실패'}
          </h3>
          <div style={{ marginBottom: '1rem' }}>
            {uploadResult.summary?.deleted > 0 && (
              <p style={{ color: 'var(--danger)', fontWeight: 500 }}>
                <strong>기존 데이터 삭제:</strong> {uploadResult.summary.deleted}건
              </p>
            )}
            <p><strong>전체 데이터:</strong> {uploadResult.summary?.total || 0}건</p>
            <p><strong>유효한 데이터:</strong> {uploadResult.summary?.valid || 0}건</p>
            <p><strong>삽입 성공:</strong> {uploadResult.summary?.inserted || 0}건</p>
            <p><strong>삽입 실패:</strong> {uploadResult.summary?.failed || 0}건</p>
          </div>

          {uploadResult.errors && uploadResult.errors.length > 0 && (
            <div style={{ marginTop: '1.5rem' }}>
              <h4 style={{ marginBottom: '0.5rem', color: 'var(--danger)' }}>오류 목록 (최대 10개):</h4>
              <div style={{ 
                maxHeight: '300px', 
                overflowY: 'auto',
                backgroundColor: 'var(--muted, #f8f9fa)',
                padding: '1rem',
                borderRadius: '4px',
                border: '1px solid var(--card-border)'
              }}>
                {uploadResult.errors.map((err: string, index: number) => (
                  <div key={index} style={{ 
                    marginBottom: '0.5rem', 
                    padding: '0.5rem',
                    backgroundColor: 'var(--card-bg)',
                    borderLeft: '3px solid #dc3545',
                    fontSize: '0.9rem'
                  }}>
                    {err}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// APT 스팸 신고 데이터 섹션
const AptSpamReportSection: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [round, setRound] = useState<string>('');
  const [period, setPeriod] = useState<string>('');
  const [availableRounds, setAvailableRounds] = useState<string[]>([]);
  const [availablePeriods, setAvailablePeriods] = useState<string[]>([]);

  useEffect(() => {
    // 사용 가능한 round와 period 목록 로드
    const loadOptions = async () => {
      try {
        const [rounds, periods] = await Promise.all([
          resultsService.getAvailableRounds(),
          resultsService.getAvailablePeriods(),
        ]);
        setAvailableRounds(rounds);
        setAvailablePeriods(periods);
        if (rounds.length > 0) setRound(rounds[0]); // 기본값 설정
      } catch (err) {
        console.error('옵션 로드 실패:', err);
      }
    };
    loadOptions();
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.csv') && !file.name.endsWith('.CSV')) {
        setError('CSV 파일만 업로드 가능합니다.');
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      setError(null);
      setUploadResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('파일을 선택해주세요.');
      return;
    }

    if (!round || round.trim() === '') {
      setError('회차(round)를 입력해주세요.');
      return;
    }

    // Period가 입력된 경우에만 검증 (선택사항)
    if (period && period.trim() !== '' && !/^\d{6}$/.test(period)) {
      setError('기간(period)은 YYYYMM 형식이어야 합니다. (예: 202512)');
      return;
    }

    setIsUploading(true);
    setError(null);
    setUploadResult(null);

    try {
      const result = await resultsService.importSpamReport(selectedFile, round, period || undefined);
      setUploadResult(result);
      setSelectedFile(null);
      // 파일 input 초기화
      const fileInput = document.getElementById('csv-file-input') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (err: any) {
      console.error('업로드 실패:', err);
      setError(err.response?.data?.message || err.message || 'CSV 파일 업로드에 실패했습니다.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div>
      <h2>APT 스팸 신고 데이터 업로드</h2>
      <div style={{ marginBottom: '2rem' }}>
        <p style={{ marginBottom: '1rem', color: 'var(--muted-foreground, #666)' }}>
          CSV 파일을 업로드하여 스팸 신고 데이터의 신고시각(report_time)과 신고분류(report_category)를 업데이트합니다.
        </p>
        <div style={{ 
          padding: '1rem', 
          backgroundColor: '#fff3cd', 
          border: '1px solid #ffc107',
          borderRadius: '4px',
          marginBottom: '1rem'
        }}>
          <p style={{ margin: 0, color: '#856404', fontSize: '0.9rem' }}>
            ⚠️ <strong>중요:</strong> 회차(round)를 반드시 선택해야 합니다. 같은 이메일이 여러 회차에 있을 경우, 선택한 회차의 데이터만 업데이트됩니다.
          </p>
        </div>
        <p style={{ marginBottom: '1rem', color: 'var(--muted-foreground, #666)', fontSize: '0.9rem' }}>
          <strong>CSV 파일 형식:</strong> 이메일주소, 구분(회사), 성명, 신고 시각, 신고 분류
        </p>
      </div>

      <div style={{ 
        background: 'var(--card-bg)', 
        padding: '2rem', 
        borderRadius: '8px', 
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        marginBottom: '2rem'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
          <div>
            <label htmlFor="spam-round-input" style={{ 
              display: 'block', 
              marginBottom: '0.5rem', 
              fontWeight: 500,
              fontSize: '1rem'
            }}>
              <span style={{ color: 'red' }}>*</span> 회차 (Round):
            </label>
            <input
              id="spam-round-input"
              list="spam-rounds-list"
              value={round}
              onChange={(e) => setRound(e.target.value)}
              disabled={isUploading}
              placeholder="선택 또는 직접 입력 (예: seah_3)"
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--card-border)',
                borderRadius: '4px',
                fontSize: '1rem',
              }}
            />
            <datalist id="spam-rounds-list">
              {availableRounds.map((r) => (
                <option key={r} value={r} />
              ))}
            </datalist>
            <p style={{ marginTop: '0.3rem', color: 'var(--muted-foreground, #666)', fontSize: '0.85rem' }}>
              💡 목록에서 선택하거나 새로운 회차를 직접 입력하세요
            </p>
          </div>

          <div>
            <label htmlFor="spam-period-input" style={{ 
              display: 'block', 
              marginBottom: '0.5rem', 
              fontWeight: 500,
              fontSize: '1rem'
            }}>
              기간 (Period, 선택사항):
            </label>
            <input
              id="spam-period-input"
              list="spam-periods-list"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              disabled={isUploading}
              placeholder="전체 또는 직접 입력 (예: 202512)"
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--card-border)',
                borderRadius: '4px',
                fontSize: '1rem',
              }}
            />
            <datalist id="spam-periods-list">
              <option value="" />
              {availablePeriods.map((p) => (
                <option key={p} value={p} />
              ))}
            </datalist>
            <p style={{ marginTop: '0.3rem', color: 'var(--muted-foreground, #666)', fontSize: '0.85rem' }}>
              💡 비워두면 전체 period 대상
            </p>
          </div>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label htmlFor="csv-file-input" style={{ 
            display: 'block', 
            marginBottom: '0.5rem', 
            fontWeight: 500,
            fontSize: '1rem'
          }}>
            CSV 파일 선택:
          </label>
          <input
            id="csv-file-input"
            type="file"
            accept=".csv,.CSV"
            onChange={handleFileChange}
            disabled={isUploading}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid var(--card-border)',
              borderRadius: '4px',
              fontSize: '1rem',
            }}
          />
          {selectedFile && (
            <p style={{ marginTop: '0.5rem', color: '#28a745', fontSize: '0.9rem' }}>
              선택된 파일: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
            </p>
          )}
        </div>

        {error && (
          <div style={{
            padding: '1rem',
            marginBottom: '1rem',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
          }}>
            {error}
          </div>
        )}

        <button
          onClick={handleUpload}
          disabled={!selectedFile || isUploading}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: selectedFile && !isUploading ? '#007bff' : '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: selectedFile && !isUploading ? 'pointer' : 'not-allowed',
            fontSize: '1rem',
            fontWeight: 500,
          }}
        >
          {isUploading ? '업로드 중...' : 'CSV 파일 업로드'}
        </button>
      </div>

      {uploadResult && (
        <div style={{
          background: 'var(--card-bg)',
          padding: '2rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        }}>
          <h3 style={{ marginBottom: '1rem' }}>업로드 결과</h3>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(2, 1fr)', 
            gap: '1rem',
            marginBottom: '1.5rem' 
          }}>
            <div style={{ 
              padding: '1rem', 
              backgroundColor: '#d4edda', 
              border: '1px solid #c3e6cb',
              borderRadius: '4px'
            }}>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#155724' }}>✅ 매칭 완료 (업데이트됨)</p>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '1.5rem', fontWeight: 'bold', color: '#155724' }}>
                {uploadResult.summary?.matched || 0}건
              </p>
            </div>

            <div style={{ 
              padding: '1rem', 
              backgroundColor: '#fff3cd', 
              border: '1px solid #ffc107',
              borderRadius: '4px'
            }}>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#856404' }}>⚠️ CSV에 없음 (업데이트 안됨)</p>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '1.5rem', fontWeight: 'bold', color: '#856404' }}>
                {uploadResult.summary?.notFoundInCsv || 0}건
              </p>
            </div>

            <div style={{ 
              padding: '1rem', 
              backgroundColor: '#f8d7da', 
              border: '1px solid #f5c6cb',
              borderRadius: '4px'
            }}>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#721c24' }}>❌ DB에 없음 (CSV만 존재)</p>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '1.5rem', fontWeight: 'bold', color: '#721c24' }}>
                {uploadResult.summary?.csvOnly || 0}건
              </p>
            </div>

            <div style={{ 
              padding: '1rem', 
              backgroundColor: '#d1ecf1', 
              border: '1px solid #bee5eb',
              borderRadius: '4px'
            }}>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#0c5460' }}>📊 통계</p>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', color: '#0c5460' }}>
                CSV: {uploadResult.summary?.totalCsv || 0}건 / DB: {uploadResult.summary?.totalDb || 0}건
              </p>
            </div>
          </div>

          {uploadResult.csvOnlyEmails && uploadResult.csvOnlyEmails.length > 0 && (
            <div style={{ marginTop: '1.5rem' }}>
              <h4 style={{ marginBottom: '0.5rem', color: 'var(--danger)' }}>DB에서 찾을 수 없는 이메일 (CSV만 존재, 최대 50개):</h4>
              <div style={{ 
                maxHeight: '300px', 
                overflowY: 'auto',
                padding: '1rem',
                backgroundColor: 'var(--muted, #f8f9fa)',
                borderRadius: '4px',
                border: '1px solid var(--card-border)'
              }}>
                <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                  {uploadResult.csvOnlyEmails.map((email: string, index: number) => (
                    <li key={index} style={{ marginBottom: '0.3rem', fontSize: '0.9rem' }}>{email}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {uploadResult.notFound && uploadResult.notFound.length > 0 && (
            <div style={{ marginTop: '1.5rem' }}>
              <h4 style={{ marginBottom: '0.5rem', color: '#ffc107' }}>CSV에서 찾을 수 없는 DB 데이터 (최대 50개):</h4>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--muted, #f8f9fa)' }}>
                      <th style={{ padding: '0.5rem', border: '1px solid var(--card-border)', textAlign: 'left' }}>이메일</th>
                      <th style={{ padding: '0.5rem', border: '1px solid var(--card-border)', textAlign: 'left' }}>회사</th>
                      <th style={{ padding: '0.5rem', border: '1px solid var(--card-border)', textAlign: 'left' }}>이름</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadResult.notFound.map((item: any, index: number) => (
                      <tr key={index}>
                        <td style={{ padding: '0.5rem', border: '1px solid var(--card-border)' }}>{item.email}</td>
                        <td style={{ padding: '0.5rem', border: '1px solid var(--card-border)' }}>{item.company}</td>
                        <td style={{ padding: '0.5rem', border: '1px solid var(--card-border)' }}>{item.name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {uploadResult.csvOnly && uploadResult.csvOnly.length > 0 && (
            <div style={{ marginTop: '1.5rem' }}>
              <h4 style={{ marginBottom: '0.5rem', color: '#17a2b8' }}>DB에서 찾을 수 없는 CSV 데이터 (최대 50개):</h4>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--muted, #f8f9fa)' }}>
                      <th style={{ padding: '0.5rem', border: '1px solid var(--card-border)', textAlign: 'left' }}>이메일</th>
                      <th style={{ padding: '0.5rem', border: '1px solid var(--card-border)', textAlign: 'left' }}>회사</th>
                      <th style={{ padding: '0.5rem', border: '1px solid var(--card-border)', textAlign: 'left' }}>이름</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadResult.csvOnly.map((item: any, index: number) => (
                      <tr key={index}>
                        <td style={{ padding: '0.5rem', border: '1px solid var(--card-border)' }}>{item.email}</td>
                        <td style={{ padding: '0.5rem', border: '1px solid var(--card-border)' }}>{item.company}</td>
                        <td style={{ padding: '0.5rem', border: '1px solid var(--card-border)' }}>{item.name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── 로그 관리 섹션 ──────────────────────────────────────────────────────────

const ACTION_BADGE_COLOR: Record<string, string> = {
  login: '#3b82f6',
  logout: '#6b7280',
  register: '#10b981',
  init_pass: '#f59e0b',
  init_pass_by_admin: '#f59e0b',
  modify_auth: '#8b5cf6',
  remove_member: '#ef4444',
  publish: '#0ea5e9',
  write_complete: '#10b981',
  write_cancel: '#6b7280',
  approve: '#0ea5e9',
  report_view: '#64748b',
  role_change: '#8b5cf6',
  admin_access: '#0ea5e9',
  export: '#14b8a6',
  password_reset_request: '#f59e0b',
  password_reset: '#f59e0b',
};

const ALL_ACTIONS = Object.keys(LOG_ACTION_LABELS) as LogAction[];

const LogManagementSection: React.FC = () => {
  const [logs, setLogs] = useState<Log[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const [filterAction, setFilterAction] = useState<LogAction | ''>('');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  const LIMIT = 50;
  const totalPages = Math.ceil(total / LIMIT);

  const loadLogs = async (targetPage: number) => {
    setIsLoading(true);
    try {
      const result = await logsService.getAll({
        page: targetPage,
        limit: LIMIT,
        action: filterAction || undefined,
        email: filterEmail || undefined,
        startDate: filterStartDate || undefined,
        endDate: filterEndDate || undefined,
      });
      setLogs(result.logs);
      setTotal(result.total);
    } catch {
      // 에러 무시 (상위 메시지 처리로 대체 가능)
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadLogs(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSearch = () => {
    setPage(1);
    loadLogs(1);
  };

  const handleReset = () => {
    setFilterAction('');
    setFilterEmail('');
    setFilterStartDate('');
    setFilterEndDate('');
    setPage(1);
    // 필터 초기화 후 재조회 (setTimeout으로 state 반영 후 실행)
    setTimeout(() => loadLogs(1), 0);
  };

  const formatTimestamp = (ts: string) => {
    const d = new Date(ts);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 style={{ margin: 0 }}>활동 로그</h2>
        <span style={{ fontSize: '0.875rem', color: 'var(--muted-foreground)' }}>총 {total.toLocaleString()}건</span>
      </div>

      {/* 필터 영역 */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem', padding: '1rem', background: 'var(--muted, #f9fafb)', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <label style={{ fontSize: '0.75rem', color: 'var(--muted-foreground)', fontWeight: 600 }}>행위</label>
          <select
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value as LogAction | '')}
            style={{ padding: '0.375rem 0.75rem', border: '1px solid var(--card-border)', borderRadius: '6px', fontSize: '0.875rem', minWidth: '160px' }}
          >
            <option value="">전체</option>
            {ALL_ACTIONS.map((a) => (
              <option key={a} value={a}>{LOG_ACTION_LABELS[a]}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <label style={{ fontSize: '0.75rem', color: 'var(--muted-foreground)', fontWeight: 600 }}>이메일</label>
          <input
            type="text"
            value={filterEmail}
            onChange={(e) => setFilterEmail(e.target.value)}
            placeholder="이메일 검색"
            style={{ padding: '0.375rem 0.75rem', border: '1px solid var(--card-border)', borderRadius: '6px', fontSize: '0.875rem', minWidth: '200px' }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <label style={{ fontSize: '0.75rem', color: 'var(--muted-foreground)', fontWeight: 600 }}>시작일</label>
          <input
            type="date"
            value={filterStartDate}
            onChange={(e) => setFilterStartDate(e.target.value)}
            style={{ padding: '0.375rem 0.75rem', border: '1px solid var(--card-border)', borderRadius: '6px', fontSize: '0.875rem' }}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <label style={{ fontSize: '0.75rem', color: 'var(--muted-foreground)', fontWeight: 600 }}>종료일</label>
          <input
            type="date"
            value={filterEndDate}
            onChange={(e) => setFilterEndDate(e.target.value)}
            style={{ padding: '0.375rem 0.75rem', border: '1px solid var(--card-border)', borderRadius: '6px', fontSize: '0.875rem' }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem' }}>
          <button
            onClick={handleSearch}
            style={{ padding: '0.375rem 1rem', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem' }}
          >
            검색
          </button>
          <button
            onClick={handleReset}
            style={{ padding: '0.375rem 1rem', background: 'var(--card-bg)', color: 'var(--content-text)', border: '1px solid var(--card-border)', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem' }}
          >
            초기화
          </button>
        </div>
      </div>

      {/* 테이블 */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted-foreground)' }}>로딩 중...</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: 'var(--muted)' }}>
                <th style={{ padding: '0.75rem', border: '1px solid #e5e7eb', textAlign: 'center', width: '60px' }}>#</th>
                <th style={{ padding: '0.75rem', border: '1px solid #e5e7eb', textAlign: 'left' }}>이메일</th>
                <th style={{ padding: '0.75rem', border: '1px solid #e5e7eb', textAlign: 'center', width: '160px' }}>행위</th>
                <th style={{ padding: '0.75rem', border: '1px solid #e5e7eb', textAlign: 'left' }}>상세</th>
                <th style={{ padding: '0.75rem', border: '1px solid #e5e7eb', textAlign: 'center', width: '180px' }}>발생 시각</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af', border: '1px solid #e5e7eb' }}>
                    로그가 없습니다.
                  </td>
                </tr>
              ) : (
                logs.map((log, idx) => (
                  <tr key={log.id} style={{ background: idx % 2 === 0 ? 'var(--card-bg)' : 'var(--muted, #f9fafb)' }}>
                    <td style={{ padding: '0.625rem', border: '1px solid #e5e7eb', textAlign: 'center', color: 'var(--muted-foreground)' }}>
                      {(page - 1) * LIMIT + idx + 1}
                    </td>
                    <td style={{ padding: '0.625rem', border: '1px solid #e5e7eb' }}>{log.email}</td>
                    <td style={{ padding: '0.625rem', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '0.2rem 0.6rem',
                        borderRadius: '9999px',
                        background: ACTION_BADGE_COLOR[log.action] || '#6b7280',
                        color: '#fff',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}>
                        {LOG_ACTION_LABELS[log.action] || log.action}
                      </span>
                    </td>
                    <td style={{ padding: '0.625rem', border: '1px solid #e5e7eb', color: 'var(--muted-foreground)' }}>
                      {log.details || '-'}
                    </td>
                    <td style={{ padding: '0.625rem', border: '1px solid #e5e7eb', textAlign: 'center', color: '#374151', whiteSpace: 'nowrap' }}>
                      {formatTimestamp(log.timestamp)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginTop: '1.25rem' }}>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{ padding: '0.375rem 0.75rem', border: '1px solid var(--card-border)', borderRadius: '6px', cursor: page === 1 ? 'not-allowed' : 'pointer', background: 'var(--card-bg)', opacity: page === 1 ? 0.4 : 1 }}
          >
            이전
          </button>
          <span style={{ fontSize: '0.875rem', color: '#374151' }}>
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{ padding: '0.375rem 0.75rem', border: '1px solid var(--card-border)', borderRadius: '6px', cursor: page === totalPages ? 'not-allowed' : 'pointer', background: 'var(--card-bg)', opacity: page === totalPages ? 0.4 : 1 }}
          >
            다음
          </button>
        </div>
      )}
    </div>
  );
};

// ─── 포털 설정 섹션 ────────────────────────────────────────────────────────────
const THEME_OPTIONS: { value: ThemeType; label: string; desc: string; sidebar: string; header: string; content: string; accent: string }[] = [
  { value: 'original',  label: '원본',             desc: '기존 스타일 (블루-그레이)',        sidebar: '#34495e', header: '#e8ecef', content: '#f5f5f5', accent: '#3498db' },
  { value: 'default',   label: 'AdminLTE Default', desc: '밝은 헤더 + 다크 그레이 사이드바', sidebar: '#343a40', header: '#ffffff', content: '#f4f6f9', accent: '#007bff' },
  { value: 'dark',      label: 'Dark',             desc: '전체 다크 네이비 테마',             sidebar: '#1f2d3d', header: '#222d32', content: '#2c3b41', accent: '#3498db' },
  { value: 'ocean',     label: 'Ocean',            desc: '청록색 계열 시원한 느낌',           sidebar: '#0d3349', header: '#e0f4f7', content: '#eef8fa', accent: '#00bcd4' },
  { value: 'slate',     label: 'Slate',            desc: '딥 네이비 + 미니멀 그레이',         sidebar: '#0f172a', header: '#f8fafc', content: '#f1f5f9', accent: '#3b82f6' },
  { value: 'rose',      label: 'Rose',             desc: '따뜻한 레드 액센트',               sidebar: '#5c1a27', header: '#fff1f2', content: '#fff5f6', accent: '#e11d48' },
  { value: 'forest',    label: 'Forest',           desc: '자연스러운 녹색 계열',             sidebar: '#132a1a', header: '#f0fdf4', content: '#f0fdf4', accent: '#22c55e' },
];

const PortalSettingsSection: React.FC = () => {
  const { settings, isAiChatEnabled, currentTheme, setTheme, designVersion, setDesignVersion, refreshSettings } = useSettings();
  const [saving, setSaving] = useState(false);
  const [themeSaving, setThemeSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleAiChatToggle = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await settingsService.update('ai_chat_enabled', isAiChatEnabled ? 'false' : 'true');
      await refreshSettings();
      setMessage({ type: 'success', text: `AI Chat이 ${isAiChatEnabled ? '비활성화' : '활성화'}되었습니다.` });
    } catch {
      setMessage({ type: 'error', text: '설정 저장에 실패했습니다.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="settings-section">
      <h2 style={{ marginBottom: '1.5rem', fontSize: '1.25rem', fontWeight: 600 }}>페이지 설정</h2>

      {message && (
        <div className={`message ${message.type}`} style={{ marginBottom: '1rem' }}>
          {message.text}
          <button onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      <div className="settings-card" style={{
        background: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '1.5rem',
      }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', color: 'var(--content-text)' }}>
          기능 관리
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 0', borderBottom: '1px solid var(--card-border)' }}>
          <div>
            <div style={{ fontWeight: 500, color: '#111827' }}>🤖 정보보호 AI Chat</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--muted-foreground)', marginTop: '0.25rem' }}>
              비활성화 시 사이드바 메뉴에서 숨겨지고 접근이 차단됩니다.
            </div>
          </div>
          <button
            onClick={handleAiChatToggle}
            disabled={saving}
            style={{
              padding: '0.5rem 1.25rem',
              borderRadius: '20px',
              border: 'none',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontWeight: 600,
              fontSize: '0.875rem',
              background: isAiChatEnabled ? '#10b981' : '#e5e7eb',
              color: isAiChatEnabled ? '#fff' : '#374151',
              transition: 'all 0.2s',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? '저장 중...' : isAiChatEnabled ? 'ON' : 'OFF'}
          </button>
        </div>
      </div>

      <div className="settings-card" style={{
        background: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '1.5rem',
      }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', color: 'var(--content-text)' }}>
          테마 설정
        </h3>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {THEME_OPTIONS.map((t) => {
            const isSelected = currentTheme === t.value;
            return (
              <button
                key={t.value}
                onClick={async () => {
                  if (isSelected || themeSaving) return;
                  setThemeSaving(true);
                  try {
                    await setTheme(t.value);
                    setMessage({ type: 'success', text: `테마가 "${t.label}"으로 변경되었습니다.` });
                  } catch {
                    setMessage({ type: 'error', text: '테마 저장에 실패했습니다.' });
                  } finally {
                    setThemeSaving(false);
                  }
                }}
                style={{
                  border: isSelected ? `2px solid ${t.accent}` : '2px solid #e5e7eb',
                  borderRadius: '10px',
                  padding: 0,
                  cursor: themeSaving ? 'wait' : isSelected ? 'default' : 'pointer',
                  background: 'none',
                  width: '160px',
                  textAlign: 'left',
                  boxShadow: isSelected ? `0 0 0 3px ${t.accent}33` : 'none',
                  transition: 'all 0.15s',
                  overflow: 'hidden',
                }}
              >
                {/* 미리보기 */}
                <div style={{ display: 'flex', height: '80px', overflow: 'hidden', borderRadius: '8px 8px 0 0' }}>
                  {/* 사이드바 */}
                  <div style={{ width: '40px', background: t.sidebar, display: 'flex', flexDirection: 'column', gap: '4px', padding: '6px 4px' }}>
                    {[...Array(4)].map((_, i) => (
                      <div key={i} style={{ height: '6px', background: i === 1 ? t.accent : 'rgba(255,255,255,0.25)', borderRadius: '2px' }} />
                    ))}
                  </div>
                  {/* 헤더 + 콘텐츠 */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ height: '18px', background: t.header, borderBottom: '1px solid var(--card-border)' }} />
                    <div style={{ flex: 1, background: t.content, padding: '4px', display: 'flex', flexWrap: 'wrap', gap: '3px', alignContent: 'flex-start' }}>
                      {[...Array(4)].map((_, i) => (
                        <div key={i} style={{ width: '45%', height: '12px', background: 'rgba(0,0,0,0.08)', borderRadius: '2px' }} />
                      ))}
                    </div>
                  </div>
                </div>
                {/* 라벨 */}
                <div style={{ padding: '0.625rem 0.75rem', background: 'var(--card-bg)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.2rem' }}>
                    {isSelected && (
                      <span style={{ color: t.accent, fontSize: '0.75rem' }}>✓</span>
                    )}
                    <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--content-text)' }}>{t.label}</span>
                  </div>
                  <span style={{ fontSize: '0.7rem', color: '#9ca3af', lineHeight: 1.3 }}>{t.desc}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* 디자인 버전 (A/B) */}
      <div className="design-version-section">
        <h3>디자인 버전</h3>
        <p>v2는 Pretendard 폰트 + Linear/Stripe 계열 스타일. 전환 즉시 반영됩니다.</p>
        <div className="design-version-options">
          {(['v1', 'v2'] as DesignVersion[]).map((v) => {
            const isSelected = designVersion === v;
            return (
              <button
                key={v}
                className={`design-version-btn${isSelected ? ' active' : ''}`}
                onClick={async () => {
                  if (isSelected) return;
                  try {
                    await setDesignVersion(v);
                  } catch {
                    alert('디자인 버전 변경에 실패했습니다.');
                  }
                }}
              >
                <div className="design-version-btn__icon">{v === 'v1' ? '🏠' : '✨'}</div>
                <div className="design-version-btn__label">{v === 'v1' ? 'v1 (현재)' : 'v2 (secuCenter)'}</div>
                {isSelected && <div className="design-version-btn__badge">적용 중</div>}
              </button>
            );
          })}
        </div>
      </div>

      {settings.length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--muted-foreground)', marginBottom: '0.5rem' }}>
            전체 설정값
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr style={{ background: 'var(--muted)' }}>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', borderBottom: '1px solid var(--card-border)' }}>Key</th>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', borderBottom: '1px solid var(--card-border)' }}>Value</th>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', borderBottom: '1px solid var(--card-border)' }}>업데이트</th>
              </tr>
            </thead>
            <tbody>
              {settings.map((s) => (
                <tr key={s.key}>
                  <td style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--card-border)', fontFamily: 'monospace' }}>{s.key}</td>
                  <td style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--card-border)' }}>{s.value}</td>
                  <td style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--card-border)', color: '#9ca3af' }}>
                    {new Date(s.updatedAt).toLocaleString('ko-KR')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── 일일보고서 관리 섹션 ─────────────────────────────────────────────────────
const STATUS_LABEL: Record<string, string> = {
  SUCCESS: '✅ 정상',
  PARTIAL: '⚠️ 일부실패',
  FAILED: '❌ 실패',
};

const DailyReportManagementSection: React.FC = () => {
  const [logs, setLogs] = useState<DailyReportCollectionLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isCollecting, setIsCollecting] = useState(false);

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    setIsLoading(true);
    try {
      const data = await dailyReportService.getCollectionLogs(200);
      setLogs(data);
    } catch {
      setMessage({ type: 'error', text: '수집 로그를 불러오는데 실패했습니다.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (period: string) => {
    if (!window.confirm(`[${period}] period 데이터를 삭제하시겠습니까?\n\n수집된 장비 데이터와 수집 로그가 모두 삭제되며 복구할 수 없습니다.`)) {
      return;
    }
    try {
      const result = await dailyReportService.deletePeriod(period);
      setMessage({ type: 'success', text: `삭제 완료 — 데이터 ${result.deletedDataCount}건, 로그 ${result.deletedLogCount}건 삭제됨` });
      loadLogs();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || '삭제에 실패했습니다.' });
    }
  };

  const describeCollectResult = (result: CollectDailyReportResult) =>
    result.status === 'SUCCESS'
      ? `[${result.period}] 수집 성공 — 장비 ${result.deviceCount ?? 0}건 (시도 ${result.attemptCount}회차)`
      : `[${result.period}] 수집 실패 — ${result.errorMessage ?? '알 수 없는 오류'} (시도 ${result.attemptCount}회차)`;

  const handleCollect = async () => {
    setIsCollecting(true);
    try {
      const result = await dailyReportService.collectNow();
      setMessage({ type: result.status === 'SUCCESS' ? 'success' : 'error', text: describeCollectResult(result) });
      loadLogs();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || '수집 요청에 실패했습니다.' });
    } finally {
      setIsCollecting(false);
    }
  };

  const formatPeriod = (period: string) => {
    if (period.length !== 10) return period;
    const date = `${period.slice(0, 4)}-${period.slice(4, 6)}-${period.slice(6, 8)}`;
    const hour = period.slice(8) === '04' ? '04:00' : '16:00';
    return `${date} ${hour}`;
  };

  return (
    <div>
      <h2>일일보고서 관리</h2>
      <p style={{ color: 'var(--muted-foreground)', fontSize: '0.875rem', marginBottom: '1rem' }}>
        수집된 period 단위로 데이터를 삭제할 수 있습니다. 삭제 후 복구가 불가능합니다.
        외부 API는 호출 시점의 실시간 스냅샷만 반환하므로, 실패한 과거 period는 재수집으로
        되살릴 수 없습니다 — 우측 버튼은 항상 당일(현재 시각 기준) 데이터만 수집합니다.
      </p>

      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
          <button onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
        <button
          onClick={handleCollect}
          disabled={isCollecting}
          style={{
            padding: '0.5rem 1rem',
            background: 'hsl(var(--primary))',
            color: 'hsl(var(--primary-foreground))',
            border: 'none',
            borderRadius: '4px',
            cursor: isCollecting ? 'not-allowed' : 'pointer',
            fontSize: '0.85rem',
          }}
        >
          {isCollecting ? '수집 중...' : '당일 데이터 수집'}
        </button>
      </div>

      {isLoading ? (
        <p>불러오는 중...</p>
      ) : logs.length === 0 ? (
        <p style={{ color: 'var(--muted-foreground)' }}>수집된 데이터가 없습니다.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ background: 'var(--muted)' }}>
              <th style={{ padding: '0.6rem 0.75rem', textAlign: 'left', borderBottom: '1px solid var(--card-border)' }}>Period</th>
              <th style={{ padding: '0.6rem 0.75rem', textAlign: 'left', borderBottom: '1px solid var(--card-border)' }}>수집 시각</th>
              <th style={{ padding: '0.6rem 0.75rem', textAlign: 'left', borderBottom: '1px solid var(--card-border)' }}>상태</th>
              <th style={{ padding: '0.6rem 0.75rem', textAlign: 'center', borderBottom: '1px solid var(--card-border)' }}>시도횟수</th>
              <th style={{ padding: '0.6rem 0.75rem', textAlign: 'center', borderBottom: '1px solid var(--card-border)' }}>삭제</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <tr key={log.period} style={{ borderBottom: '1px solid var(--card-border)' }}>
                <td style={{ padding: '0.5rem 0.75rem', fontFamily: 'monospace' }}>{formatPeriod(log.period)}</td>
                <td style={{ padding: '0.5rem 0.75rem', color: 'var(--muted-foreground)' }}>
                  {log.collectedAt ? new Date(log.collectedAt).toLocaleString('ko-KR') : '—'}
                </td>
                <td style={{ padding: '0.5rem 0.75rem' }}>
                  {STATUS_LABEL[log.collectionStatus] ?? log.collectionStatus}
                </td>
                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>{log.attemptCount}</td>
                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>
                  <button
                    onClick={() => handleDelete(log.period)}
                    style={{
                      padding: '0.25rem 0.75rem',
                      background: '#ef4444',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                    }}
                  >
                    삭제
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ─── 보안 솔루션 예외처리 관리 섹션 ────────────────────────────────────────────

const SOLUTION_LABELS: Record<SolutionType, string> = {
  NAC: 'NAC',
  '유해사이트': '유해사이트',
  DLP: 'DLP', // IPT-624 — '세아홀딩스 DLP'에서 변경
};

const INACTIVE_SOLUTIONS = ['세아제강지주 DLP', 'EDR'];

type ExceptionFormData = {
  company: string;
  solution: SolutionType | '';
  exceptionItem: string;
  exceptionDetail: string;
  department: string;
  name: string;
  reason: string;
  approvalDocNo: string;
  applyDate: string;
  startDate: string;
  endDate: string;
  actionType: string;
  exceptionContent: string;
  remark: string;
};

const EMPTY_FORM: ExceptionFormData = {
  company: '',
  solution: '',
  exceptionItem: '',
  exceptionDetail: '',
  department: '',
  name: '',
  reason: '',
  approvalDocNo: '',
  applyDate: '',
  startDate: '',
  endDate: '',
  actionType: '',
  exceptionContent: '',
  remark: '',
};

const EXCEPTION_PAGE_LIMIT = 20;

const fldStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.45rem 0.75rem',
  borderRadius: '6px',
  border: '1.5px solid var(--border, #d1d5db)',
  background: 'var(--input-bg, #fff)',
  fontSize: '0.875rem',
  color: 'var(--foreground, #111)',
  outline: 'none',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '0.3rem',
  fontWeight: 600,
  fontSize: '0.8rem',
  color: 'var(--muted-foreground, #555)',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
};

const SOLUTION_BADGE: Record<SolutionType, { bg: string; color: string }> = {
  NAC:     { bg: '#dbeafe', color: '#1d4ed8' },
  '유해사이트': { bg: '#fef3c7', color: '#92400e' },
  DLP:     { bg: '#ede9fe', color: '#5b21b6' },
};

export const SecurityExceptionManagementSection: React.FC = () => {
  const [items, setItems] = useState<ExceptionItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [companies, setCompanies] = useState<ExceptionCompany[]>([]);

  // 검색 필터
  const [filterSolution, setFilterSolution] = useState<SolutionType | ''>('');
  const [filterCompany, setFilterCompany] = useState('');
  const [filterExceptionItem, setFilterExceptionItem] = useState('');
  const [filterExceptionDetail, setFilterExceptionDetail] = useState('');
  const [filterName, setFilterName] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  // 페이징
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(total / EXCEPTION_PAGE_LIMIT));

  // 폼
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<ExceptionItem | null>(null);
  const [form, setForm] = useState<ExceptionFormData>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // 계열사가 사용하는 솔루션 목록
  const availableSolutions = (() => {
    if (!form.company) return [] as SolutionType[];
    const co = companies.find((c) => c.companyName === form.company);
    if (!co) return [] as SolutionType[];
    return (['NAC', '유해사이트', 'DLP'] as SolutionType[]).filter(
      (s) => co[SOLUTION_USEYN_KEY[s]] === 'Y'
    );
  })();

  const exceptionItemOptions: string[] = (() => {
    if (form.solution === 'NAC') return [...NAC_EXCEPTION_ITEMS];
    if (form.solution === '유해사이트') return [...SITE_EXCEPTION_ITEMS];
    if (form.solution === 'DLP') return DLP_EXCEPTION_ITEMS;
    return [];
  })();

  const exceptionDetailOptions: string[] = (() => {
    if (form.solution === '유해사이트') return [...SITE_EXCEPTION_DETAILS];
    if (form.solution === 'DLP' && form.exceptionItem) return [...(DLP_EXCEPTION_MAP[form.exceptionItem] ?? [])];
    return [];
  })();

  const loadItems = async (targetPage = page) => {
    setIsLoading(true);
    try {
      const result = await securityExceptionService.getAll({
        solution: filterSolution || undefined,
        company: filterCompany || undefined,
        exceptionItem: filterExceptionItem || undefined,
        exceptionDetail: filterExceptionDetail || undefined,
        name: filterName || undefined,
        startDate: filterStartDate || undefined,
        endDate: filterEndDate || undefined,
        page: targetPage,
        limit: EXCEPTION_PAGE_LIMIT,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch {
      setMessage({ type: 'error', text: '목록을 불러오는 데 실패했습니다.' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    securityExceptionService.getCompanies().then(setCompanies).catch(() => {});
  }, []);

  useEffect(() => {
    setPage(1);
    loadItems(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSolution, filterCompany, filterExceptionItem, filterExceptionDetail, filterName, filterStartDate, filterEndDate]);

  useEffect(() => {
    loadItems(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const resetFilters = () => {
    setFilterSolution('');
    setFilterCompany('');
    setFilterExceptionItem('');
    setFilterExceptionDetail('');
    setFilterName('');
    setFilterStartDate('');
    setFilterEndDate('');
  };

  const openAddForm = () => { setEditingItem(null); setForm(EMPTY_FORM); setShowForm(true); };

  const openEditForm = (item: ExceptionItem) => {
    setEditingItem(item);
    setForm({
      company: item.company || '',
      solution: item.solution,
      exceptionItem: item.exceptionItem || '',
      exceptionDetail: item.exceptionDetail || '',
      department: item.department || '',
      name: item.name || '',
      reason: item.reason || '',
      approvalDocNo: item.approvalDocNo || '',
      applyDate: item.applyDate ? item.applyDate.slice(0, 10) : '',
      startDate: item.startDate ? item.startDate.slice(0, 10) : '',
      endDate: item.endDate ? item.endDate.slice(0, 10) : '',
      actionType: item.actionType || '',
      exceptionContent: item.exceptionContent || '',
      remark: item.remark || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (item: ExceptionItem) => {
    if (!window.confirm('이 예외처리 항목을 삭제하시겠습니까?')) return;
    try {
      if (item.solution === 'NAC') await securityExceptionService.deleteNac(item.id);
      else if (item.solution === '유해사이트') await securityExceptionService.deleteSite(item.id);
      else if (item.solution === 'DLP') await securityExceptionService.deleteDlp(item.id);
      setMessage({ type: 'success', text: '삭제되었습니다.' });
      loadItems();
    } catch {
      setMessage({ type: 'error', text: '삭제에 실패했습니다.' });
    }
  };

  const handleSave = async () => {
    if (!form.company) { setMessage({ type: 'error', text: '계열사를 선택해주세요.' }); return; }
    if (!form.solution) { setMessage({ type: 'error', text: '솔루션을 선택해주세요.' }); return; }
    if (!form.exceptionItem) { setMessage({ type: 'error', text: '예외항목을 선택해주세요.' }); return; }
    setIsSaving(true);
    try {
      if (form.solution === 'NAC') {
        const dto: NacExceptionRequest = { exceptionItem: form.exceptionItem, company: form.company, department: form.department, name: form.name, reason: form.reason, approvalDocNo: form.approvalDocNo, applyDate: form.applyDate || undefined, startDate: form.startDate || undefined, endDate: form.endDate || undefined, actionType: form.actionType, exceptionContent: form.exceptionContent, remark: form.remark };
        if (editingItem) await securityExceptionService.updateNac(editingItem.id, dto);
        else await securityExceptionService.createNac(dto);
      } else if (form.solution === '유해사이트') {
        const dto: SiteExceptionRequest = { exceptionItem: form.exceptionItem, exceptionDetail: form.exceptionDetail, company: form.company, department: form.department, name: form.name, reason: form.reason, approvalDocNo: form.approvalDocNo, applyDate: form.applyDate || undefined, startDate: form.startDate || undefined, endDate: form.endDate || undefined, actionType: form.actionType, exceptionContent: form.exceptionContent, remark: form.remark };
        if (editingItem) await securityExceptionService.updateSite(editingItem.id, dto);
        else await securityExceptionService.createSite(dto);
      } else if (form.solution === 'DLP') {
        const dto: SecurityDlpExceptionRequest = { exceptGroup: form.exceptionItem, exceptDetail: form.exceptionDetail, reqCompany: form.company, reqDepartment: form.department, reqName: form.name, reason: form.reason, approval: form.approvalDocNo, applyDate: form.applyDate || undefined, endDate: form.endDate || undefined, remark: form.remark };
        if (editingItem) await securityExceptionService.updateDlp(editingItem.id, dto);
        else await securityExceptionService.createDlp(dto);
      }
      setMessage({ type: 'success', text: editingItem ? '수정되었습니다.' : '등록되었습니다.' });
      setShowForm(false);
      loadItems();
    } catch {
      setMessage({ type: 'error', text: '저장에 실패했습니다.' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      {/* 헤더 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700 }}>보안 솔루션 예외처리 관리</h2>
          <span style={{ fontSize: '0.82rem', color: 'var(--muted-foreground, #888)', fontWeight: 500 }}>총 {total.toLocaleString()}건</span>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={openAddForm} style={{ padding: '0.4rem 1rem', borderRadius: '6px', background: 'var(--accent, #2563eb)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>
            + 예외처리 추가
          </button>
        </div>
      </div>
      <p style={{ color: 'var(--muted-foreground, #888)', marginBottom: '1rem', fontSize: '0.85rem' }}>
        NAC · 유해사이트 · DLP 예외처리 신청 및 통합 관리 — solution 컬럼에 NAC / 유해사이트 / DLP 중 하나를 입력합니다.
      </p>

      {message && (
        <div style={{ marginBottom: '1rem', padding: '0.6rem 1rem', borderRadius: '6px', border: `1.5px solid ${message.type === 'success' ? '#16a34a' : '#dc2626'}`, background: message.type === 'success' ? '#f0fdf4' : '#fef2f2', color: message.type === 'success' ? '#16a34a' : '#dc2626', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.875rem' }}>
          {message.text}
          <button onClick={() => setMessage(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', color: 'inherit' }}>×</button>
        </div>
      )}

      {/* 검색 바 */}
      <ExSearchBar>
        <UiSelect value={filterSolution} onChange={(e) => setFilterSolution(e.target.value as SolutionType | '')} className="w-auto">
          <option value="">전체 솔루션</option>
          <option value="NAC">NAC</option>
          <option value="유해사이트">유해사이트</option>
          <option value="DLP">DLP</option>
        </UiSelect>
        <UiSelect value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)} className="w-auto min-w-[130px]">
          <option value="">전체 계열사</option>
          {companies.map((c) => <option key={c.orgCode} value={c.companyName}>{c.companyName}</option>)}
        </UiSelect>
        <UiSelect value={filterExceptionItem} onChange={(e) => setFilterExceptionItem(e.target.value)} className="w-auto min-w-[130px]">
          <option value="">전체 예외항목</option>
          {(filterSolution === 'NAC' ? [...NAC_EXCEPTION_ITEMS]
            : filterSolution === '유해사이트' ? [...SITE_EXCEPTION_ITEMS]
            : filterSolution === 'DLP' ? DLP_EXCEPTION_ITEMS
            : [...NAC_EXCEPTION_ITEMS, ...SITE_EXCEPTION_ITEMS, ...DLP_EXCEPTION_ITEMS]
          ).map((item) => <option key={item} value={item}>{item}</option>)}
        </UiSelect>
        <UiInput type="text" placeholder="예외상세 검색" value={filterExceptionDetail} onChange={(e) => setFilterExceptionDetail(e.target.value)} className="w-[120px]" />
        <UiInput type="text" placeholder="이름 검색" value={filterName} onChange={(e) => setFilterName(e.target.value)} className="w-[100px]" />
        <UiInput type="date" value={filterStartDate} onChange={(e) => setFilterStartDate(e.target.value)} className="w-[150px]" title="적용일 시작" />
        <span className="text-sm text-muted-foreground">~</span>
        <UiInput type="date" value={filterEndDate} onChange={(e) => setFilterEndDate(e.target.value)} className="w-[150px]" title="적용일 종료" />
        <UiButton type="button" variant="outline" size="sm" onClick={resetFilters}>초기화</UiButton>
      </ExSearchBar>

      {/* 추가/수정 폼 */}
      {showForm && (
        <div style={{ border: '1.5px solid var(--border, #e2e8f0)', borderRadius: '10px', marginBottom: '1.5rem', background: 'var(--card-bg, #fff)', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
          {/* 폼 헤더 */}
          <div style={{ background: 'var(--accent, #2563eb)', padding: '0.85rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem' }}>
              {editingItem ? '예외처리 수정' : '예외처리 추가'}
            </span>
            <button onClick={() => setShowForm(false)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '4px', color: '#fff', cursor: 'pointer', padding: '0.2rem 0.5rem', fontSize: '1rem', lineHeight: 1 }}>×</button>
          </div>

          <div style={{ padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

            {/* ── 섹션 1: 솔루션 선택 ── */}
            <fieldset style={{ margin: 0, padding: '1rem 1.1rem', border: '1.5px solid #bfdbfe', borderRadius: '8px', background: '#eff6ff' }}>
              <legend style={{ padding: '0 0.5rem', fontSize: '0.75rem', fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.05em' }}>솔루션 선택</legend>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.875rem' }}>
                <div style={formFieldBox}>
                  <label style={labelStyle}>계열사 *</label>
                  <select value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value, solution: '', exceptionItem: '', exceptionDetail: '' })} style={fldStyle} disabled={!!editingItem}>
                    <option value="">계열사 선택</option>
                    {companies.map((c) => <option key={c.orgCode} value={c.companyName}>{c.companyName}</option>)}
                  </select>
                </div>
                <div style={formFieldBox}>
                  <label style={labelStyle}>솔루션 *</label>
                  <select value={form.solution} onChange={(e) => setForm({ ...form, solution: e.target.value as SolutionType | '', exceptionItem: '', exceptionDetail: '' })} style={{ ...fldStyle, opacity: !form.company ? 0.5 : 1 }} disabled={!!editingItem || !form.company}>
                    <option value="">솔루션 선택</option>
                    {availableSolutions.map((s) => <option key={s} value={s}>{SOLUTION_LABELS[s]}</option>)}
                    {INACTIVE_SOLUTIONS.map((s) => <option key={s} value={s} disabled>{s} (추후 예정)</option>)}
                  </select>
                </div>
                <div style={formFieldBox}>
                  <label style={labelStyle}>예외항목 *</label>
                  <select value={form.exceptionItem} onChange={(e) => setForm({ ...form, exceptionItem: e.target.value, exceptionDetail: '' })} style={{ ...fldStyle, opacity: !form.solution ? 0.5 : 1 }} disabled={!form.solution}>
                    <option value="">예외항목 선택</option>
                    {exceptionItemOptions.map((item) => <option key={item} value={item}>{item}</option>)}
                  </select>
                </div>
                {form.solution !== 'NAC' && (
                  <div style={formFieldBox}>
                    <label style={labelStyle}>예외상세</label>
                    {exceptionDetailOptions.length > 0 ? (
                      <select value={form.exceptionDetail} onChange={(e) => setForm({ ...form, exceptionDetail: e.target.value })} style={{ ...fldStyle, opacity: !form.exceptionItem ? 0.5 : 1 }} disabled={!form.exceptionItem}>
                        <option value="">선택</option>
                        {exceptionDetailOptions.map((d) => <option key={d} value={d}>{d}</option>)}
                      </select>
                    ) : (
                      <input type="text" value={form.exceptionDetail} onChange={(e) => setForm({ ...form, exceptionDetail: e.target.value })} style={fldStyle} placeholder="직접 입력" />
                    )}
                  </div>
                )}
              </div>
            </fieldset>

            {/* ── 섹션 2: 신청자 정보 ── */}
            <fieldset style={{ margin: 0, padding: '1rem 1.1rem', border: '1.5px solid var(--border, #e2e8f0)', borderRadius: '8px', background: 'var(--card-bg, #f8fafc)' }}>
              <legend style={{ padding: '0 0.5rem', fontSize: '0.75rem', fontWeight: 700, color: 'var(--muted-foreground, #64748b)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>신청자 정보</legend>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.875rem' }}>
                <div style={formFieldBox}>
                  <label style={labelStyle}>부서</label>
                  <input type="text" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} style={fldStyle} />
                </div>
                <div style={formFieldBox}>
                  <label style={labelStyle}>이름</label>
                  <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={fldStyle} />
                </div>
                <div style={formFieldBox}>
                  <label style={labelStyle}>예외처리 사유</label>
                  <input type="text" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} style={fldStyle} />
                </div>
                <div style={formFieldBox}>
                  <label style={labelStyle}>결재문서번호</label>
                  <input type="text" value={form.approvalDocNo} onChange={(e) => setForm({ ...form, approvalDocNo: e.target.value })} style={fldStyle} />
                </div>
              </div>
            </fieldset>

            {/* ── 섹션 3: 처리 기간 ── */}
            <fieldset style={{ margin: 0, padding: '1rem 1.1rem', border: '1.5px solid var(--border, #e2e8f0)', borderRadius: '8px', background: 'var(--card-bg, #f8fafc)' }}>
              <legend style={{ padding: '0 0.5rem', fontSize: '0.75rem', fontWeight: 700, color: 'var(--muted-foreground, #64748b)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>처리 기간</legend>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.875rem' }}>
                <div style={formFieldBox}>
                  <label style={labelStyle}>예외처리 적용일</label>
                  <input type="date" value={form.applyDate} onChange={(e) => setForm({ ...form, applyDate: e.target.value })} style={fldStyle} />
                </div>
                {form.solution !== 'DLP' && (
                  <div style={formFieldBox}>
                    <label style={labelStyle}>기간 시작</label>
                    <input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} style={fldStyle} />
                  </div>
                )}
                <div style={formFieldBox}>
                  <label style={labelStyle}>{form.solution === 'DLP' ? '기한 (미입력 시 영구)' : '기간 종료'}</label>
                  <input type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} style={fldStyle} />
                </div>
                <div style={formFieldBox}>
                  <label style={labelStyle}>작업</label>
                  <div style={{ display: 'flex', gap: '1rem', paddingTop: '0.5rem' }}>
                    {['수정', '삭제'].map((v) => (
                      <label key={v} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer', fontSize: '0.875rem' }}>
                        <input type="radio" name="actionType" value={v} checked={form.actionType === v} onChange={(e) => setForm({ ...form, actionType: e.target.value })} />
                        {v}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </fieldset>

            {/* ── 섹션 4: 상세 내용 ── */}
            <fieldset style={{ margin: 0, padding: '1rem 1.1rem', border: '1.5px solid var(--border, #e2e8f0)', borderRadius: '8px', background: 'var(--card-bg, #f8fafc)' }}>
              <legend style={{ padding: '0 0.5rem', fontSize: '0.75rem', fontWeight: 700, color: 'var(--muted-foreground, #64748b)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>상세 내용</legend>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.875rem' }}>
                <div style={formFieldBox}>
                  <label style={labelStyle}>예외내역</label>
                  <textarea value={form.exceptionContent} onChange={(e) => setForm({ ...form, exceptionContent: e.target.value })} rows={3} style={{ ...fldStyle, resize: 'vertical' }} />
                </div>
                <div style={formFieldBox}>
                  <label style={labelStyle}>비고</label>
                  <textarea value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} rows={3} style={{ ...fldStyle, resize: 'vertical' }} />
                </div>
              </div>
            </fieldset>

          </div>

          {/* 폼 푸터 */}
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', padding: '0.875rem 1.5rem', borderTop: '1.5px solid var(--border, #e2e8f0)', background: 'var(--card-bg, #f8fafc)' }}>
            <button onClick={() => setShowForm(false)} style={{ padding: '0.45rem 1.25rem', borderRadius: '6px', border: '1.5px solid var(--border, #d1d5db)', background: '#fff', cursor: 'pointer', fontSize: '0.875rem' }}>취소</button>
            <button onClick={handleSave} disabled={isSaving} style={{ padding: '0.45rem 1.5rem', borderRadius: '6px', background: 'var(--accent, #2563eb)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600, opacity: isSaving ? 0.6 : 1 }}>
              {isSaving ? '저장 중...' : (editingItem ? '수정 저장' : '추가')}
            </button>
          </div>
        </div>
      )}

      {/* 목록 테이블 */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--muted-foreground, #888)' }}>불러오는 중...</div>
      ) : items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--muted-foreground, #888)', border: '1.5px dashed var(--border, #e5e7eb)', borderRadius: '8px' }}>
          등록된 예외처리 항목이 없습니다.
        </div>
      ) : (
        <div style={{ border: '1.5px solid var(--border, #e5e7eb)', borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr style={{ background: 'var(--table-header-bg, #f1f5f9)', borderBottom: '2px solid var(--border, #e2e8f0)' }}>
                  {['솔루션', '예외항목', '예외상세', '계열사', '부서', '이름', '예외처리 사유', '적용일', '기한', '등록일', '관리'].map((h) => (
                    <th key={h} style={{ ...exThStyle }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={`${item.solution}-${item.id}`} style={{ background: idx % 2 === 0 ? 'var(--card-bg, #fff)' : 'var(--table-row-alt, #fafafa)', borderBottom: '1px solid var(--border, #e5e7eb)' }}>
                    <td style={exTdStyle}>
                      <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '0.72rem', fontWeight: 700, background: SOLUTION_BADGE[item.solution].bg, color: SOLUTION_BADGE[item.solution].color, whiteSpace: 'nowrap' }}>
                        {item.solution}
                      </span>
                    </td>
                    <td style={exTdStyle}>{item.exceptionItem || '-'}</td>
                    <td style={exTdStyle}>{item.exceptionDetail || '-'}</td>
                    <td style={{ ...exTdStyle, fontWeight: 500 }}>{item.company || '-'}</td>
                    <td style={exTdStyle}>{item.department || '-'}</td>
                    <td style={exTdStyle}>{item.name || '-'}</td>
                    <td style={{ ...exTdStyle, maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.reason || ''}>{item.reason || '-'}</td>
                    <td style={{ ...exTdStyle, whiteSpace: 'nowrap' }}>{item.applyDate ? item.applyDate.slice(0, 10) : '-'}</td>
                    <td style={{ ...exTdStyle, whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                      {item.startDate
                        ? `${item.startDate.slice(0, 10)} ~ ${item.endDate ? item.endDate.slice(0, 10) : '영구'}`
                        : item.endDate
                          ? `~ ${item.endDate.slice(0, 10)}`
                          : '-'}
                    </td>
                    <td style={{ ...exTdStyle, whiteSpace: 'nowrap', color: 'var(--muted-foreground, #888)' }}>
                      {item.createdAt ? new Date(item.createdAt).toLocaleDateString('ko-KR') : '-'}
                    </td>
                    <td style={exTdStyle}>
                      <div style={{ display: 'flex', gap: '0.35rem' }}>
                        <button onClick={() => openEditForm(item)} style={{ padding: '2px 8px', fontSize: '0.72rem', borderRadius: '4px', border: '1.5px solid var(--border, #d1d5db)', background: 'transparent', cursor: 'pointer', fontWeight: 500 }}>수정</button>
                        <button onClick={() => handleDelete(item)} style={{ padding: '2px 8px', fontSize: '0.72rem', borderRadius: '4px', border: '1.5px solid #fca5a5', color: '#dc2626', background: 'transparent', cursor: 'pointer', fontWeight: 500 }}>삭제</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 페이징 */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', padding: '0.75rem', borderTop: '1.5px solid var(--border, #e5e7eb)', background: 'var(--card-bg, #fff)' }}>
              <button onClick={() => setPage(1)} disabled={page === 1} style={{ padding: '0.3rem 0.6rem', borderRadius: '5px', border: '1.5px solid var(--border, #d1d5db)', background: 'transparent', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1, fontSize: '0.8rem' }}>«</button>
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: '0.3rem 0.6rem', borderRadius: '5px', border: '1.5px solid var(--border, #d1d5db)', background: 'transparent', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1, fontSize: '0.8rem' }}>‹</button>
              <span style={{ fontSize: '0.82rem', fontWeight: 500, padding: '0 0.5rem', color: 'var(--foreground, #111)' }}>{page} / {totalPages}</span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ padding: '0.3rem 0.6rem', borderRadius: '5px', border: '1.5px solid var(--border, #d1d5db)', background: 'transparent', cursor: page === totalPages ? 'not-allowed' : 'pointer', opacity: page === totalPages ? 0.4 : 1, fontSize: '0.8rem' }}>›</button>
              <button onClick={() => setPage(totalPages)} disabled={page === totalPages} style={{ padding: '0.3rem 0.6rem', borderRadius: '5px', border: '1.5px solid var(--border, #d1d5db)', background: 'transparent', cursor: page === totalPages ? 'not-allowed' : 'pointer', opacity: page === totalPages ? 0.4 : 1, fontSize: '0.8rem' }}>»</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const formFieldBox: React.CSSProperties = {
  background: '#fff',
  border: '1px solid var(--border, #e2e8f0)',
  borderRadius: '6px',
  padding: '0.625rem 0.75rem',
};

const exThStyle: React.CSSProperties = {
  padding: '0.6rem 0.75rem',
  textAlign: 'left',
  fontWeight: 700,
  fontSize: '0.75rem',
  color: 'var(--muted-foreground, #64748b)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  whiteSpace: 'nowrap',
};

const exTdStyle: React.CSSProperties = {
  padding: '0.55rem 0.75rem',
  verticalAlign: 'middle',
};

const thStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  textAlign: 'left',
  fontWeight: 600,
  borderBottom: '2px solid var(--border, #ddd)',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  verticalAlign: 'middle',
};

export default AdminPage;

